import React, {
    useState,
    useRef,
    useEffect,
    useLayoutEffect,
    useMemo,
    useCallback,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { date as formatDate, quantity } from "@/utils/format";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { Icon } from "@/components/Icon";
import { developmentApi } from "@/lib/api";
import { AskAiModal } from "@/pages/Developments/AskAiModal";
import { MessageGestures } from "@/pages/Developments/MessageGestures";
import { DownloadModal, type PdfMeta } from "@/components/DownloadModal";

import { DatePickerModal } from "@/components/DatePickerModal";
import { type DateRange } from "@/components/DatePicker";
import { Alert } from "@/components/Alert";
import { RichText } from "@/components/RichText";
import { Markdown } from "@/components/Markdown";
import { CompletePhaseModal } from "@/pages/Developments/CompletePhaseModal";
import { Modal } from "@/components/Modal";
import { SearchInput } from "@/components/SearchInput";
import { Button } from "@/components/Button";
import { Badge } from "@/components/Badge";
import { Select } from "@/components/Select";
import { Tooltip } from "@/components/Tooltip";
import { AttachmentThumbnail } from "@/components/AttachmentThumbnail";
import {
    ImageLightboxModal,
    type LightboxItem,
    isImageFile,
} from "@/components/ImageLightboxModal";
import { FileViewer } from "@/components/FileViewer";
import { MicrophoneButton } from "@/components/MicrophoneButton";
import { backendApi } from "@/utils/URL";
import { initEcho } from "@/utils/Echo";
import {
    ATTACHMENT_DRAG_MIME,
    buildAttachmentReference,
    cleanAttachmentReferencesForCopy,
    expandAttachmentReferences,
    insertAttachmentReference,
    parseAttachmentDragPayload,
    type AttachmentReference,
} from "@/utils/attachmentReference";
import {
    canUserSignWorkflowStep,
    getCurrentWorkflowStep,
    getStepRoleLabelKey,
    getWorkflowStepProgress,
    jumpDesign,
    jumpTasks,
    type WorkflowStep,
} from "@/utils/stepRole";
import {
    date,
    html2text,
    htmlToPlainText,
    hasRichFormatting,
    formatFileSize,
    price,
} from "@/utils/format";

interface Message {
    id: number;
    user_id?: number;
    sender_name?: string;
    sender_role?: string;
    content: string;
    type?: string;
    is_internal?: boolean;
    metadata?: {
        model?: string;
        step_key?: string;
        step_title?: string;
        [key: string]: unknown;
    };
    reply_to_message_id?: number | null;
    reply_to?: { id: number; sender_name?: string; content: string } | null;
    attachments?: Array<{
        id: number;
        name: string;
        mime_type?: string;
        file_size?: number;
    }>;
    created_at: string;
}

interface DevelopmentChatFeedProps {
    development: {
        id: number;
        code?: string;
        title?: string;
        description?: string;
        status?: string;
        priority?: string;
        created_at?: string;
        client_id?: number;
        client?: { id: number; name: string };
        assigned?: { id: number; name: string };
        messages?: Message[];
        attachments?: Array<{
            id: number;
            name: string;
            mime_type?: string;
            file_size?: number;
        }>;
        participants?: Array<{ user_id: number; role: "viewer" | "editor" }>;
        steps?: WorkflowStep[];
    };
    onUpdate: (data: Record<string, unknown>) => void;
    targetMessage?: { id: number; timestamp: number } | null;
    isRightPanelOpen?: boolean;
    onToggleRightPanel?: () => void;
    showRightPanelToggle?: boolean;
    openDesignModalTrigger?: {
        timestamp: number;
        requirementDocId?: number;
    } | null;
}

// if true, enter key sends the message and shift+enter is new line. if false, enter is new line
const ENTER_SEND = true;

const TYPE_FILTER_ICONS: Record<
    "all" | "it" | "client" | "ai" | "internal",
    string
> = {
    all: "users",
    it: "computer",
    client: "user",
    ai: "sparkles",
    internal: "lock",
};

const isPrivateMessage = (message: Message): boolean =>
    Boolean(
        message.is_internal ||
            message.type === "ai_response" ||
            message.type === "ai_design_proposal"
    );

const isComposerEmpty = (content: string): boolean => {
    const trimmed = content.trim();
    if (!trimmed) {
        return true;
    }
    const textOnly = trimmed
        .replace(/<br\s*\/?>/gi, "")
        .replace(/<\/?p[^>]*>/gi, "")
        .replace(/<\/?div[^>]*>/gi, "")
        .replace(/&nbsp;/gi, " ")
        .replace(/<[^>]+>/g, "")
        .trim();
    return textOnly.length === 0;
};

const normalizeComposerBody = (content: string): string => {
    if (isComposerEmpty(content)) {
        return "";
    }

    if (!hasRichFormatting(content)) {
        return htmlToPlainText(content);
    }

    return content;
};

const hasRichTextMarkup = (content: string): boolean => {
    if (isComposerEmpty(content)) {
        return false;
    }

    return hasRichFormatting(content);
};

interface CollapsibleChatMessageProps {
    content: string;
    msgId?: number;
    variant?: "ai" | "ai_client" | "internal" | "mine" | "other";
}

const CollapsibleChatMessage: React.FC<CollapsibleChatMessageProps> = ({
    content,
    msgId,
    variant = "other",
}) => {
    const { t } = useTranslation("global");
    const [expanded, setExpanded] = useState<boolean>(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const [isLong, setIsLong] = useState<boolean>(false);
    const [contentHeight, setContentHeight] = useState<number>(0);

    useEffect(() => {
        const element = contentRef.current;
        if (!element) {
            return;
        }

        const measure = (): void => {
            setContentHeight(element.scrollHeight);
            setIsLong(element.scrollHeight > 160);
        };

        measure();

        const observer = new ResizeObserver(measure);
        observer.observe(element);

        return () => {
            observer.disconnect();
        };
    }, [content]);

    let gradientClass =
        "bg-gradient-to-t from-slate-100/90 dark:from-zinc-900/90 to-transparent";
    const gradientStyle: React.CSSProperties | undefined = undefined;
    let buttonClass = "btn-secondary";
    let markdownClass =
        "select-text [&_a]:!no-underline [&_a]:text-primary dark:[&_a]:text-primary-400 hover:[&_a]:text-primary-hover hover:[&_a]:!no-underline [&_button]:!no-underline hover:[&_button]:!no-underline";
    let isInverted = false;

    switch (variant) {
        case "ai":
            gradientClass =
                "bg-gradient-to-t from-purple-50 dark:from-purple-900/30 to-transparent";
            buttonClass = "btn-purple";
            markdownClass =
                "text-xs text-purple-900 dark:text-purple-100 leading-relaxed font-sans select-text";
            isInverted = false;
            break;

        case "ai_client":
            gradientClass =
                "bg-gradient-to-t from-sky-50 dark:from-sky-950/25 to-transparent";
            buttonClass =
                "inline-flex items-center justify-center gap-2 rounded-xl transition-all select-none cursor-pointer px-4 py-2 text-sm font-medium bg-sky-600 hover:bg-sky-700 text-white shadow-xs border border-transparent";
            markdownClass =
                "text-xs text-slate-900 dark:text-zinc-100 leading-relaxed font-sans select-text";
            isInverted = false;
            break;

        case "internal":
            gradientClass =
                "bg-gradient-to-t from-amber-50 dark:from-amber-950/40 to-transparent";
            buttonClass = "btn-warninginverted";
            markdownClass = "select-text";
            isInverted = false;
            break;

        case "mine":
            gradientClass =
                "bg-gradient-to-t from-blue-100 dark:from-blue-950/90 to-transparent";
            buttonClass = "btn-primary";
            markdownClass =
                "select-text text-slate-900 dark:text-zinc-100 leading-relaxed font-sans [&_a]:text-blue-600 dark:[&_a]:text-blue-400 hover:[&_a]:text-blue-700 hover:[&_a]:!no-underline [&_button]:!no-underline hover:[&_button]:!no-underline";
            isInverted = false;
            break;

        case "other":
        default:
            gradientClass =
                "bg-gradient-to-t from-slate-100/90 dark:from-zinc-900/90 to-transparent";
            buttonClass = "btn-secondary";
            markdownClass =
                "select-text [&_a]:!no-underline [&_a]:text-primary dark:[&_a]:text-primary-400 hover:[&_a]:text-primary-hover hover:[&_a]:!no-underline [&_button]:!no-underline hover:[&_button]:!no-underline";
            isInverted = false;
            break;
    }

    return (
        <div className="w-full">
            <div
                ref={contentRef}
                className="overflow-hidden transition-[max-height] duration-500 ease-in-out relative"
                style={{
                    maxHeight: expanded
                        ? `${contentHeight}px`
                        : isLong
                          ? "120px"
                          : "none",
                }}
            >
                <Markdown inverted={isInverted} className={markdownClass}>
                    {content}
                </Markdown>
                {isLong && !expanded && (
                    <div
                        className={`absolute bottom-0 left-0 right-0 h-10 pointer-events-none ${gradientClass}`}
                        style={gradientStyle}
                    />
                )}
            </div>
            {isLong && (
                <div className="flex justify-center mt-3">
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            setExpanded(!expanded);
                        }}
                        aria-expanded={expanded}
                        className={buttonClass}
                    >
                        {expanded ? t("common.showless") : t("common.showmore")}
                        <Icon
                            value={expanded ? "chevronUp" : "chevronDown"}
                            size={14}
                        />
                    </button>
                </div>
            )}
        </div>
    );
};

const CollapsibleAiMessage: React.FC<{
    content: string;
    msgId?: number;
    isAiClient?: boolean;
}> = ({ content, msgId, isAiClient }) => {
    return (
        <CollapsibleChatMessage
            content={content}
            msgId={msgId}
            variant={isAiClient ? "ai_client" : "ai"}
        />
    );
};

export const DevelopmentChatFeed: React.FC<DevelopmentChatFeedProps> = ({
    development,
    onUpdate,
    targetMessage,
    isRightPanelOpen = true,
    onToggleRightPanel,
    showRightPanelToggle = false,
    openDesignModalTrigger,
}) => {
    const { t } = useTranslation("global");
    const { user, token, isIt, isAdmin, isRoot, isClient, isDevelopment } =
        useAuth();
    const isViewerIt = Boolean(isIt || isDevelopment || isAdmin || isRoot);
    const { showSuccess, showError } = useToast();
    const [body, setBody] = useState<string>("");
    const [baseBodyForRecording, setBaseBodyForRecording] = useState<
        string | null
    >(null);
    const [composerAttachmentRefs, setComposerAttachmentRefs] = useState<
        AttachmentReference[]
    >([]);
    const [isInternal, setIsInternal] = useState<boolean>(false);
    const [sending, setSending] = useState<boolean>(false);
    const [isRecordingAudio, setIsRecordingAudio] = useState<boolean>(false);
    const [showAiModal, setShowAiModal] = useState<boolean>(false);
    const [showDownloadModal, setShowDownloadModal] = useState<boolean>(false);
    const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
    const [showStepSignModal, setShowStepSignModal] = useState<boolean>(false);
    const [isSigningStep, setIsSigningStep] = useState<boolean>(false);
    const [expandedMessage, setExpandedMessage] = useState<Message | null>(
        null
    );
    const expandedMessageButtonRef = useRef<DOMRect | null>(null);

    useEffect(() => {
        if (openDesignModalTrigger) {
            setShowAiModal(true);
            setAiSource(null);
            setPrefilledRequirementDocId(
                openDesignModalTrigger.requirementDocId
            );
        }
    }, [openDesignModalTrigger]);

    const updateRef = useRef(onUpdate);
    updateRef.current = onUpdate;
    useEffect(() => {
        if (!token) return;
        const echo = initEcho(
            token,
            backendApi() || "http://localhost:8000/api"
        );
        if (!echo) return;
        let active = true;
        let timer: ReturnType<typeof setTimeout>;
        const refresh = () => {
            clearTimeout(timer);
            timer = setTimeout(async () => {
                try {
                    const res = await developmentApi.get(development.id);
                    if (active && res.result) updateRef.current(res.data);
                } catch {
                    /* Reconnect will retry the authorized detail request. */
                }
            }, 150);
        };
        const channel = echo.private(`development.${development.id}`);

        channel
            .listen(".message.created", refresh)
            .listen(".development.updated", refresh)
            .subscribed(refresh);

        return () => {
            active = false;
            clearTimeout(timer);
            echo.leave(`development.${development.id}`);
        };
    }, [development.id, token]);

    useEffect(() => {
        if (!isFullscreen) {
            return;
        }
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [isFullscreen]);

    useEffect(() => {
        if (!isFullscreen) {
            return;
        }

        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key !== "Escape") {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            setIsFullscreen(false);
            (document.activeElement as HTMLElement | null)?.blur();
        };

        document.addEventListener("keydown", onKeyDown, true);
        return () => document.removeEventListener("keydown", onKeyDown, true);
    }, [isFullscreen]);

    useLayoutEffect(() => {
        const rect = chatContainerRef.current?.getBoundingClientRect();
        if (!rect) return;

        const prev = previousChatBounds.current;
        if (
            prev &&
            prev.fullscreen !== isFullscreen &&
            !window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ) {
            chatContainerRef.current?.animate(
                [
                    {
                        transformOrigin: "top left",
                        transform: `translate(${prev.rect.x - rect.x}px, ${prev.rect.y - rect.y}px) scale(${prev.rect.width / rect.width}, ${prev.rect.height / rect.height})`,
                        borderRadius: prev.fullscreen ? "0" : "1rem",
                    },
                    {
                        transformOrigin: "top left",
                        transform: "none",
                        borderRadius: isFullscreen ? "0" : "1rem",
                    },
                ],
                { duration: 280, easing: "cubic-bezier(.2,.8,.2,1)" }
            );
        }

        previousChatBounds.current = { rect, fullscreen: isFullscreen };
    }, [isFullscreen]);

    const [isTextareaFullscreen, setIsTextareaFullscreen] =
        useState<boolean>(false);
    const [isClosingComposerFullscreen, setIsClosingComposerFullscreen] =
        useState<boolean>(false);
    const composerContainerRef = useRef<HTMLFormElement>(null);
    const fullscreenComposerRef = useRef<HTMLDivElement>(null);
    const composerOriginRect = useRef<DOMRect | null>(null);
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const previousChatBounds = useRef<{
        rect: DOMRect;
        fullscreen: boolean;
    } | null>(null);
    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const pendingFilePreviewUrls = useMemo(() => {
        return new Map(
            pendingFiles
                .filter((file) => isImageFile(file.name, file.type))
                .map((file) => [file, URL.createObjectURL(file)])
        );
    }, [pendingFiles]);

    useEffect(() => {
        return () => {
            pendingFilePreviewUrls.forEach((url) => URL.revokeObjectURL(url));
        };
    }, [pendingFilePreviewUrls]);
    const [replyTo, setReplyTo] = useState<Message | null>(null);
    const isInternalLockedByQuote = Boolean(
        isIt && replyTo && isPrivateMessage(replyTo)
    );

    useEffect(() => {
        if (isInternalLockedByQuote) {
            setIsInternal(true);
        }
    }, [isInternalLockedByQuote]);

    const clearReplyTo = (): void => {
        setReplyTo(null);
    };

    const toggleInternalMode = (): void => {
        if (isInternalLockedByQuote) {
            return;
        }
        setIsInternal((current) => !current);
    };

    const renderComposerModeToggle = (extraClassName = ""): React.ReactNode => {
        if (!isIt) {
            return null;
        }

        const label = isInternal
            ? t("developments.detail.mode_internal")
            : t("developments.detail.mode_public");
        const tooltipTitle = isInternalLockedByQuote
            ? t("developments.detail.quote_private_warning")
            : isInternal
              ? t("developments.detail.mode_internal_desc")
              : t("developments.detail.mode_public_desc");

        return (
            <Tooltip placement="top" title={tooltipTitle}>
                <span className="inline-flex">
                    <button
                        type="button"
                        onClick={toggleInternalMode}
                        disabled={isInternalLockedByQuote}
                        aria-label={label}
                        className={`px-3 py-1! rounded-xl font-semibold flex items-center gap-1.5 border transition-all ${extraClassName} ${
                            isInternalLockedByQuote
                                ? "opacity-60 cursor-not-allowed"
                                : "cursor-pointer"
                        } ${isInternal ? "btn-warning" : "btn-success"}`}
                    >
                        <Icon
                            value={isInternal ? "lock" : "unlock"}
                            size={13}
                        />
                        <span>{label}</span>
                    </button>
                </span>
            </Tooltip>
        );
    };

    const [aiSource, setAiSource] = useState<Message | null>(null);
    const [copiedMsgId, setCopiedMsgId] = useState<number | null>(null);
    const [lightboxItems, setLightboxItems] = useState<LightboxItem[]>([]);
    const [lightboxIndex, setLightboxIndex] = useState<number>(0);
    const [lightboxId, setLightboxId] = useState<number | string | undefined>(
        undefined
    );
    const [showLightbox, setShowLightbox] = useState<boolean>(false);
    const [fileViewerTarget, setFileViewerTarget] = useState<{
        attachmentId?: number;
        filename: string;
        mimeType?: string;
        fileSize?: number;
        fileDate?: string | number;
        loadBlob: () => Promise<Blob>;
        directUrl?: string;
    } | null>(null);
    const [isSelectionMode, setIsSelectionMode] = useState<boolean>(false);
    const [selectedMessageIds, setSelectedMessageIds] = useState<Set<number>>(
        new Set()
    );
    const [isCopiedSelected, setIsCopiedSelected] = useState<boolean>(false);
    const [attachmentToDelete, setAttachmentToDelete] = useState<{
        id: number;
        name: string;
    } | null>(null);
    const [isDeletingAttachment, setIsDeletingAttachment] =
        useState<boolean>(false);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [typeFilter, setTypeFilter] = useState<string>("all");
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");
    const [showDatePickerModal, setShowDatePickerModal] =
        useState<boolean>(false);
    const [pendingRange, setPendingRange] = useState<DateRange>({
        start: null,
        end: null,
    });
    const [prefilledRequirementDocId, setPrefilledRequirementDocId] = useState<
        number | undefined
    >(undefined);
    const [statsModalMessage, setStatsModalMessage] = useState<Message | null>(
        null
    );

    const [showFilters, setShowFilters] = useState<boolean>(false);

    useEffect(() => {
        if (!isIt && (typeFilter === "ai" || typeFilter === "internal")) {
            setTypeFilter("all");
        }
    }, [isIt, typeFilter]);

    const currentParticipant = development.participants?.find(
        (p) => p.user_id === user?.id
    );
    const isCancelled = development.status === "cancelled";
    const isEditor =
        !isCancelled &&
        (currentParticipant
            ? currentParticipant.role === "editor"
            : isIt || isDevelopment || development.client_id === user?.id);
    const workflowSteps = development.steps ?? [];
    const currentWorkflowStep = useMemo(
        () => getCurrentWorkflowStep(workflowSteps),
        [workflowSteps]
    );
    const isDesignPhase = currentWorkflowStep?.tag === "design";
    const isRequirementsPhase =
        currentWorkflowStep?.tag === "requirements" ||
        currentWorkflowStep?.tag === "request";

    const signableCurrentStep = useMemo(() => {
        if (!currentWorkflowStep) {
            return null;
        }

        const stepKey =
            currentWorkflowStep.tag || currentWorkflowStep.step_key || "";
        if (!stepKey) {
            return null;
        }

        const canSign = canUserSignWorkflowStep(stepKey, {
            isCurrent: true,
            isEditor,
            isClient,
            isIt,
        });

        return canSign ? { step: currentWorkflowStep, stepKey } : null;
    }, [currentWorkflowStep, isEditor, isClient, isIt]);

    const getWorkflowStepLabel = (step: WorkflowStep): string =>
        step.tag
            ? t(`developments.detail.timeline.steps.${step.tag}`)
            : step.title;

    const openStepSignModal = (): void => {
        setIsSigningStep(false);
        setShowStepSignModal(true);
    };

    const renderCompletePhaseButton = (
        onPrimaryHeader = false
    ): React.ReactNode => {
        if (!signableCurrentStep) {
            return null;
        }

        const phaseLabel = getWorkflowStepLabel(signableCurrentStep.step);
        const stepProgress = getWorkflowStepProgress(
            workflowSteps,
            signableCurrentStep.step
        );
        const buttonClassName = onPrimaryHeader
            ? "inline-flex items-center gap-1.5 rounded-xl border px-2.5 py-1.5 transition-colors cursor-pointer border-white/35 bg-white/5 text-white/85 hover:border-white hover:bg-white/15 hover:text-white"
            : "inline-flex items-center gap-1.5 rounded-xl border px-2.5 py-1.5 transition-colors cursor-pointer border-slate-300 bg-white text-slate-600 dark:border-zinc-600 dark:bg-zinc-800/50 dark:text-zinc-300 hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-800 dark:hover:border-emerald-800/80 dark:hover:bg-emerald-950/40 dark:hover:text-emerald-300";

        return (
            <Tooltip
                placement="top"
                title={
                    <span className="block max-w-[240px] text-left">
                        <span className="block">
                            {t(
                                "developments.detail.chat_complete_phase_tooltip",
                                { phase: phaseLabel }
                            )}
                        </span>
                        <span className="mt-0.5 block font-normal text-slate-300">
                            {t(
                                "developments.detail.chat_complete_phase_tooltip_desc",
                                {
                                    role: t(
                                        getStepRoleLabelKey(
                                            signableCurrentStep.stepKey
                                        )
                                    ),
                                }
                            )}
                        </span>
                    </span>
                }
            >
                <button
                    type="button"
                    onClick={openStepSignModal}
                    className={`group ${buttonClassName}`}
                    aria-label={t(
                        "developments.detail.chat_complete_phase_tooltip",
                        { phase: phaseLabel }
                    )}
                >
                    <span className="relative inline-flex items-center justify-center shrink-0">
                        <span className="group-hover:!hidden">
                            <Icon value="unlock" size={16} />
                        </span>
                        <span className="!hidden group-hover:!inline-flex">
                            <Icon value="check" size={16} />
                        </span>
                    </span>

                    {stepProgress && (
                        <span className="text-sm font-bold leading-none tracking-tight">
                            {t(
                                "developments.detail.chat_complete_phase_prefix"
                            ) +
                                " " +
                                phaseLabel}
                        </span>
                    )}
                </button>
            </Tooltip>
        );
    };

    const requirementDocsCount = useMemo(
        () =>
            (development.attachments ?? []).filter(
                (a) =>
                    a.category === "requirements_document" &&
                    (!a.is_internal || a.name.toLowerCase().endsWith(".pdf"))
            ).length,
        [development.attachments]
    );

    const designDocsCount = useMemo(
        () =>
            (development.attachments ?? []).filter(
                (a) =>
                    a.category === "design" ||
                    a.name.toLowerCase().includes("diseno") ||
                    a.name.toLowerCase().includes("diseño")
            ).length,
        [development.attachments]
    );

    const [isGeneratingDoc, setIsGeneratingDoc] = useState<boolean>(false);

    const handleGenerateRequirements = async (): Promise<void> => {
        setIsGeneratingDoc(true);

        try {
            const genRes = await developmentApi.generateRequirements(
                development.id
            );
            if (!genRes.result) {
                throw new Error(
                    genRes.message ||
                        t("developments.detail.requirements_documents_error")
                );
            }

            const detail = await developmentApi.get(development.id);
            if (detail.result) {
                onUpdate(detail.data);
            }
            showSuccess(
                t("developments.detail.requirements_documents_success")
            );
        } catch (err) {
            showError(
                err instanceof Error
                    ? err.message
                    : t("developments.detail.requirements_documents_error")
            );
        } finally {
            setIsGeneratingDoc(false);
        }
    };

    const handleConfirmSignStep = async (
        shouldGenerateDoc: boolean = false,
        createTask: boolean = true
    ): Promise<void> => {
        if (!signableCurrentStep) {
            return;
        }

        setIsSigningStep(true);

        try {
            if (shouldGenerateDoc) {
                const genRes = await developmentApi.generateRequirements(
                    development.id
                );
                if (!genRes.result) {
                    throw new Error(
                        genRes.message ||
                            t(
                                "developments.detail.requirements_documents_error"
                            )
                    );
                }
            }

            const res = await developmentApi.signStep(
                development.id,
                signableCurrentStep.stepKey,
                undefined,
                true,
                createTask
            );

            if (res.result) {
                if (createTask) {
                    window.dispatchEvent(new CustomEvent("vigepra:process-enqueued"));
                }
                setShowStepSignModal(false);
                onUpdate(res.data);

                if (createTask) {
                    void (async () => {
                        const previousTaskCount = development.tasks?.length ?? 0;
                        for (let attempt = 0; attempt < 8; attempt += 1) {
                            await new Promise((resolve) =>
                                window.setTimeout(resolve, 750)
                            );
                            const detail = await developmentApi.get(
                                development.id
                            );
                            if (
                                detail.result &&
                                (detail.data?.tasks?.length ?? 0) >
                                    previousTaskCount
                            ) {
                                onUpdate(detail.data);
                                return;
                            }
                        }
                    })();
                }
            } else {
                showError(
                    res.message || t("developments.detail.step_sign_error")
                );
            }
        } catch (err) {
            showError(
                err instanceof Error
                    ? err.message
                    : t("developments.detail.step_sign_error")
            );
        } finally {
            setIsSigningStep(false);
        }
    };

    const handleConfirmDeleteAttachment = async (): Promise<void> => {
        if (!attachmentToDelete) {
            return;
        }

        setIsDeletingAttachment(true);

        try {
            const res = await developmentApi.deleteAttachment(
                development.id,
                attachmentToDelete.id
            );
            if (res.result) {
                setAttachmentToDelete(null);
                const updatedDev = await developmentApi.get(development.id);
                if (updatedDev.result) {
                    onUpdate(updatedDev.data);
                }
            }
        } catch {
            // Keep modal open or let user retry
        } finally {
            setIsDeletingAttachment(false);
        }
    };

    const fileInputRef = useRef<HTMLInputElement>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const [isDragOverComposer, setIsDragOverComposer] =
        useState<boolean>(false);
    const [highlightedMsgId, setHighlightedMsgId] = useState<number | null>(
        null
    );

    const scrollToTop = (): void => {
        messagesContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    };

    const scrollToMessage = useCallback(
        (
            targetMsgId: number,
            retries: number = 8,
            highlight: boolean = true
        ): void => {
            const el = document.getElementById(`message-${targetMsgId}`);
            if (el && messagesContainerRef.current) {
                const container = messagesContainerRef.current;
                const scrollPos =
                    el.offsetTop -
                    container.offsetTop -
                    container.clientHeight / 2 +
                    el.clientHeight / 2;
                container.scrollTo({
                    top: Math.max(0, scrollPos),
                    behavior: "smooth",
                });
                el.scrollIntoView({ behavior: "smooth", block: "center" });

                if (highlight) {
                    setHighlightedMsgId(null);
                    window.requestAnimationFrame(() => {
                        setHighlightedMsgId(targetMsgId);
                    });
                    window.setTimeout(() => {
                        setHighlightedMsgId((current) =>
                            current === targetMsgId ? null : current
                        );
                    }, 3200);
                }
                return;
            }

            if (retries > 0) {
                window.setTimeout(() => {
                    scrollToMessage(targetMsgId, retries - 1, highlight);
                }, 80);
            }
        },
        []
    );

    useEffect(() => {
        if (targetMessage?.id) {
            if (typeFilter !== "all") {
                setTypeFilter("all");
            }
            if (searchQuery) {
                setSearchQuery("");
            }
            scrollToMessage(targetMessage.id);
        }
    }, [targetMessage, typeFilter, searchQuery, scrollToMessage]);

    const resetComposerDrag = (): void => {
        setIsDragOverComposer(false);
    };

    const isDragOverComposerRef = useRef<boolean>(false);
    useEffect(() => {
        isDragOverComposerRef.current = isDragOverComposer;
    }, [isDragOverComposer]);

    useEffect(() => {
        const reset = (): void => {
            setIsDragOverComposer(false);
        };

        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === "Escape") {
                reset();
            }
        };

        const onDragEnd = (): void => {
            reset();
        };

        const onDocumentDragLeave = (event: DragEvent): void => {
            if (!isDragOverComposerRef.current) {
                return;
            }
            const related = event.relatedTarget as Node | null;
            if (!related) {
                reset();
                return;
            }
            const { clientX, clientY } = event;
            if (
                clientX <= 0 ||
                clientY <= 0 ||
                clientX >= window.innerWidth - 1 ||
                clientY >= window.innerHeight - 1
            ) {
                reset();
            }
        };

        const onWindowDragLeave = (event: DragEvent): void => {
            if (!isDragOverComposerRef.current) {
                return;
            }
            const related = event.relatedTarget as Node | null;
            if (!related) {
                reset();
            }
        };

        document.addEventListener("keydown", onKeyDown, true);
        document.addEventListener("dragend", onDragEnd, true);
        document.addEventListener("dragleave", onDocumentDragLeave, true);
        window.addEventListener("dragend", onDragEnd);
        window.addEventListener("dragleave", onWindowDragLeave);

        return () => {
            document.removeEventListener("keydown", onKeyDown, true);
            document.removeEventListener("dragend", onDragEnd, true);
            document.removeEventListener(
                "dragleave",
                onDocumentDragLeave,
                true
            );
            window.removeEventListener("dragend", onDragEnd);
            window.removeEventListener("dragleave", onWindowDragLeave);
        };
    }, []);

    const composerDragCounter = useRef<number>(0);

    const handleComposerDragEnter = (e: React.DragEvent<HTMLElement>): void => {
        e.preventDefault();
        const hasFiles = e.dataTransfer.types.includes("Files");
        const hasAttachment =
            e.dataTransfer.types.includes(ATTACHMENT_DRAG_MIME);
        if (!hasFiles && !hasAttachment) {
            return;
        }
        composerDragCounter.current += 1;
        setIsDragOverComposer(true);
    };

    const handleComposerDragLeave = (e: React.DragEvent<HTMLElement>): void => {
        e.preventDefault();
        composerDragCounter.current -= 1;
        if (composerDragCounter.current === 0) {
            resetComposerDrag();
        }
    };

    const handleComposerDragOver = (e: React.DragEvent<HTMLElement>): void => {
        e.preventDefault();
        if (
            e.dataTransfer.types.includes("Files") ||
            e.dataTransfer.types.includes(ATTACHMENT_DRAG_MIME)
        ) {
            e.dataTransfer.dropEffect = "copy";
        }
    };

    const handleComposerDrop = (e: React.DragEvent<HTMLElement>): void => {
        e.preventDefault();
        e.stopPropagation();
        if (!isEditor) return;
        composerDragCounter.current = 0;
        resetComposerDrag();

        const attachmentPayload = parseAttachmentDragPayload(
            e.dataTransfer.getData(ATTACHMENT_DRAG_MIME)
        );
        if (attachmentPayload) {
            // Add the reference so it's linked if needed
            setComposerAttachmentRefs((current) => {
                if (current.some((ref) => ref.id === attachmentPayload.id)) {
                    return current;
                }
                return [...current, attachmentPayload];
            });
            setBody((current) =>
                insertAttachmentReference(
                    current,
                    attachmentPayload.id,
                    attachmentPayload.name
                )
            );
            return;
        }

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const files = Array.from(e.dataTransfer.files);
            setPendingFiles((current) => [...current, ...files]);
        }
    };

    const renderComposerDropOverlay = (): React.ReactNode => {
        if (!isDragOverComposer) {
            return null;
        }
        return (
            <div
                className={`absolute inset-0 ${isTextareaFullscreen ? "z-[120000]" : "z-20"} flex items-center justify-center pointer-events-none border-2 border-dashed border-primary/50 bg-primary/15 dark:bg-primary/20 ${isTextareaFullscreen ? "" : "rounded-b-2xl"}`}
            >
                <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/90 dark:bg-dark-box/90 text-primary dark:text-primary-400 font-semibold text-sm shadow-sm">
                    <Icon value="paperclip" size={18} />
                    <span>{t("developments.detail.drop_files")}</span>
                </div>
            </div>
        );
    };

    useEffect(() => {
        if (!isTextareaFullscreen) {
            setBody((current) => normalizeComposerBody(current));
        }
    }, [isTextareaFullscreen]);

    const openComposerFullscreen = useCallback((): void => {
        if (composerContainerRef.current) {
            composerOriginRect.current =
                composerContainerRef.current.getBoundingClientRect();
        }
        setIsTextareaFullscreen(true);
    }, []);

    const closeComposerFullscreen = useCallback((): void => {
        setBody((current) => normalizeComposerBody(current));
        composerDragCounter.current = 0;
        resetComposerDrag();

        if (isClosingComposerFullscreen) {
            return;
        }

        const element = fullscreenComposerRef.current;
        const target =
            composerContainerRef.current?.getBoundingClientRect() ||
            composerOriginRect.current;

        if (
            element &&
            target &&
            !window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ) {
            setIsClosingComposerFullscreen(true);
            const rect = element.getBoundingClientRect();
            const anim = element.animate(
                [
                    {
                        transformOrigin: "top left",
                        transform: "none",
                        borderRadius: "0",
                        opacity: 1,
                    },
                    {
                        transformOrigin: "top left",
                        transform: `translate(${target.x - rect.x}px, ${target.y - rect.y}px) scale(${target.width / rect.width}, ${target.height / rect.height})`,
                        borderRadius: "1rem",
                        opacity: 0,
                    },
                ],
                { duration: 280, easing: "cubic-bezier(.2,.8,.2,1)" }
            );

            anim.onfinish = () => {
                setIsClosingComposerFullscreen(false);
                setIsTextareaFullscreen(false);
            };
        } else {
            setIsTextareaFullscreen(false);
        }
    }, [isClosingComposerFullscreen]);

    useLayoutEffect(() => {
        if (!isTextareaFullscreen) {
            return;
        }

        const element = fullscreenComposerRef.current;
        const origin = composerOriginRect.current;
        if (
            element &&
            origin &&
            !window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ) {
            const rect = element.getBoundingClientRect();
            element.animate(
                [
                    {
                        transformOrigin: "top left",
                        transform: `translate(${origin.x - rect.x}px, ${origin.y - rect.y}px) scale(${origin.width / rect.width}, ${origin.height / rect.height})`,
                        borderRadius: "1rem",
                        opacity: 0,
                    },
                    {
                        transformOrigin: "top left",
                        transform: "none",
                        borderRadius: "0",
                        opacity: 1,
                    },
                ],
                { duration: 280, easing: "cubic-bezier(.2,.8,.2,1)" }
            );
        }
    }, [isTextareaFullscreen]);

    const composerTextareaRef = useRef<HTMLTextAreaElement>(null);
    const adjustComposerHeight = useCallback(() => {
        const el = composerTextareaRef.current;
        const msgContainer = messagesContainerRef.current;
        if (!el) {
            return;
        }

        // Check if the chat feed is currently scrolled to the bottom
        let wasAtBottom = false;
        if (msgContainer) {
            wasAtBottom =
                Math.abs(
                    msgContainer.scrollHeight -
                        msgContainer.clientHeight -
                        msgContainer.scrollTop
                ) < 10;
        }

        // Save the browser's natural scroll position and current height
        const correctScrollTop = el.scrollTop;
        const oldHeight = el.style.height;

        el.style.height = "1px";
        const newHeight = Math.min(180, Math.max(40, el.scrollHeight));
        el.style.height = `${newHeight}px`;

        // Restore the natural scroll position
        el.scrollTop = correctScrollTop;

        // If the chat was at the bottom, and the textarea height changed, scroll chat to bottom to keep it visible
        if (wasAtBottom && msgContainer && oldHeight !== `${newHeight}px`) {
            msgContainer.scrollTop = msgContainer.scrollHeight;
        }
    }, []);

    useEffect(() => {
        adjustComposerHeight();
    }, [body, adjustComposerHeight]);

    const handleTextareaChange = (
        e: React.ChangeEvent<HTMLTextAreaElement>
    ): void => {
        setBody(e.target.value);

        // We do NOT manually adjust height here, because the useEffect on [body] will do it
        // and we want to let the browser naturally scroll first.
    };

    const messages = React.useMemo(() => {
        const list = development?.messages || [];
        if (isClient) {
            return list
                .filter((m) => !m.is_internal)
                .map((m) => ({
                    ...m,
                    attachments: m.attachments?.filter(
                        (att) => !att.is_internal
                    ),
                }));
        }

        return list;
    }, [development?.messages, isClient]);
    const orderedMessages = React.useMemo(() => {
        return [...messages].sort(
            (a, b) =>
                new Date(a.created_at).getTime() -
                new Date(b.created_at).getTime()
        );
    }, [messages]);

    // Client senders for the unified filter
    const clientSenders = useMemo(() => {
        const seen = new Set<string>();
        const result: string[] = [];
        messages.forEach((msg) => {
            const isSystem =
                msg.type === "system" ||
                msg.type === "step_event" ||
                msg.sender_role === "system";
            const isAi =
                msg.type === "ai_response" ||
                msg.type === "ai_design_proposal" ||
                msg.sender_role === "ai" ||
                Boolean(msg.metadata?.model) ||
                (msg.sender_name?.toLowerCase().includes("ia") ?? false) ||
                (msg.sender_name?.toLowerCase().includes("asistente") ?? false);
            const isIt = msg.sender_role === "it" || Boolean(msg.is_internal);

            if (
                !isSystem &&
                !isAi &&
                !isIt &&
                msg.sender_name &&
                !seen.has(msg.sender_name)
            ) {
                const nameLower = msg.sender_name.toLowerCase();
                if (
                    !nameLower.includes("ia") &&
                    !nameLower.includes("asistente") &&
                    !nameLower.includes("sistema")
                ) {
                    seen.add(msg.sender_name);
                    result.push(msg.sender_name);
                }
            }
        });
        return result.sort((a, b) => a.localeCompare(b));
    }, [messages]);

    const filteredMessages = React.useMemo(() => {
        let baseList = orderedMessages;
        if (isClient) {
            const hiddenTags = ["frontend", "backend", "tests"];
            baseList = baseList
                .filter((msg) => {
                    const isSys =
                        msg.type === "system" ||
                        msg.type === "step_event" ||
                        msg.sender_role === "system";
                    if (isSys) {
                        const stepTag =
                            msg.metadata?.step_tag ||
                            msg.metadata?.target_step_tag;
                        if (hiddenTags.includes(stepTag)) {
                            if (
                                stepTag === "tests" &&
                                msg.metadata?.auto === "step_completed"
                            ) {
                                return true;
                            }
                            return false;
                        }
                        if (stepTag === "tasks") {
                            if (msg.metadata?.auto === "step_started") {
                                return true;
                            }
                            return false;
                        }
                    }
                    return true;
                })
                .map((msg) => {
                    const isSys =
                        msg.type === "system" ||
                        msg.type === "step_event" ||
                        msg.sender_role === "system";
                    if (isSys) {
                        const stepTag =
                            msg.metadata?.step_tag ||
                            msg.metadata?.target_step_tag;
                        if (
                            (stepTag === "tasks" &&
                                msg.metadata?.auto === "step_started") ||
                            (stepTag === "tests" &&
                                msg.metadata?.auto === "step_completed")
                        ) {
                            return {
                                ...msg,
                                metadata: {
                                    ...msg.metadata,
                                    _original_step_tag: stepTag,
                                    step_tag: msg.metadata?.step_tag
                                        ? "development"
                                        : undefined,
                                    target_step_tag: msg.metadata
                                        ?.target_step_tag
                                        ? "development"
                                        : undefined,
                                },
                            };
                        }
                    }
                    return msg;
                });
        }

        return baseList.filter((msg) => {
            const isSystem =
                msg.type === "system" ||
                msg.type === "step_event" ||
                msg.sender_role === "system";
            const isAi =
                msg.type === "ai_response" ||
                msg.type === "ai_design_proposal" ||
                msg.sender_role === "ai" ||
                Boolean(msg.metadata?.model) ||
                (msg.sender_name?.toLowerCase().includes("ia") ?? false) ||
                (msg.sender_name?.toLowerCase().includes("asistente") ?? false);
            const isItMsg =
                !isAi &&
                !isSystem &&
                (msg.sender_role === "it" || Boolean(msg.is_internal));
            const isClientMsg = !isAi && !isSystem && !isItMsg;

            switch (typeFilter) {
                case "it":
                    if (!isItMsg || msg.is_internal) {
                        return false;
                    }
                    break;
                case "ai":
                    if (!isAi) {
                        return false;
                    }
                    break;
                case "internal":
                    if (!msg.is_internal || isAi || isSystem) {
                        return false;
                    }
                    break;
                default:
                    if (typeFilter.startsWith("client:")) {
                        const clientName = typeFilter.substring(7);
                        if (!isClientMsg || msg.sender_name !== clientName) {
                            return false;
                        }
                    }
                    break;
            }

            if (searchQuery.trim()) {
                const query = searchQuery.toLowerCase().trim();
                const contentMatch = msg.content?.toLowerCase().includes(query);
                const senderMatch = msg.sender_name
                    ?.toLowerCase()
                    .includes(query);
                const attachmentMatch = msg.attachments?.some((att) =>
                    att.name.toLowerCase().includes(query)
                );

                if (!contentMatch && !senderMatch && !attachmentMatch) {
                    return false;
                }
            }

            if (startDate) {
                const msgDate = new Date(msg.created_at);
                const start = new Date(`${startDate}T00:00:00`);
                if (msgDate < start) {
                    return false;
                }
            }

            if (endDate) {
                const msgDate = new Date(msg.created_at);
                const end = new Date(`${endDate}T23:59:59.999`);
                if (msgDate > end) {
                    return false;
                }
            }

            return true;
        });
    }, [orderedMessages, typeFilter, searchQuery, startDate, endDate]);

    const hasActiveFilters = Boolean(
        searchQuery.trim() || typeFilter !== "all" || startDate || endDate
    );

    const handleResetFilters = (): void => {
        setSearchQuery("");
        setTypeFilter("all");
        setStartDate("");
        setEndDate("");
    };

    const allChatImageAttachments = React.useMemo(() => {
        const list: Array<{ id: number; name: string; mime_type?: string }> =
            [];
        const seen = new Set<number>();

        const pushAttachment = (att: {
            id: number;
            name: string;
            mime_type?: string;
            is_internal?: boolean;
        }): void => {
            if (
                seen.has(att.id) ||
                !isImageFile(att.name, att.mime_type) ||
                (isClient && att.is_internal)
            ) {
                return;
            }

            seen.add(att.id);
            list.push(att);
        };

        development.attachments?.forEach(pushAttachment);
        messages.forEach((m) => {
            m.attachments?.forEach(pushAttachment);
        });
        return list;
    }, [development.attachments, messages, isClient]);

    const attachmentById = React.useMemo(() => {
        const map = new Map<
            number,
            {
                id: number;
                name: string;
                mime_type?: string;
                file_size?: number;
                created_at?: string;
                is_internal?: boolean;
            }
        >();

        development.attachments?.forEach((att) => {
            if (isClient && att.is_internal) {
                return;
            }

            map.set(att.id, att);
        });
        messages.forEach((message) => {
            if (isClient && message.is_internal) {
                return;
            }

            message.attachments?.forEach((att) => {
                if (isClient && att.is_internal) {
                    return;
                }

                map.set(att.id, {
                    ...att,
                    created_at: att.created_at || message.created_at,
                });
            });
        });
        return map;
    }, [development.attachments, messages, isClient]);

    const attachmentByName = React.useMemo(() => {
        const map = new Map<
            string,
            {
                id: number;
                name: string;
                mime_type?: string;
                file_size?: number;
                created_at?: string;
            }
        >();

        attachmentById.forEach((att) => {
            map.set(att.name.trim().toLowerCase(), att);
        });
        return map;
    }, [attachmentById]);

    const handleMessageContentClick = (
        event: React.MouseEvent<HTMLElement>
    ): void => {
        const target = event.target as HTMLElement;
        const attachmentButton = target.closest(
            "[data-attachment-id], [data-attachment-name]"
        );
        if (!attachmentButton) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        const attachmentIdAttr =
            attachmentButton.getAttribute("data-attachment-id");
        const attachmentNameAttr = attachmentButton.getAttribute(
            "data-attachment-name"
        );

        if (attachmentIdAttr) {
            const attachmentId = Number(attachmentIdAttr);
            const attachment = attachmentById.get(attachmentId);
            if (attachment) {
                handleAttachmentClick(attachment);
                return;
            }
        }

        if (attachmentNameAttr) {
            const cleanName = attachmentNameAttr.trim().toLowerCase();
            const attachment = attachmentByName.get(cleanName);
            if (attachment) {
                handleAttachmentClick(attachment);
                return;
            }
        }
    };

    const lastScrollMsgId = useRef<number | null>(null);

    const scrollToBottom = (): void => {
        if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTo({
                top: messagesContainerRef.current.scrollHeight,
                behavior: "smooth",
            });
        }

        const chatEl = document.getElementById("chat-feed-container");
        if (chatEl && !isFullscreen) {
            chatEl.scrollIntoView({ behavior: "smooth", block: "end" });
        }
    };

    useEffect(() => {
        if (orderedMessages.length === 0) return;

        const lastMsg = orderedMessages[orderedMessages.length - 1];
        if (lastScrollMsgId.current === lastMsg.id) {
            return;
        }
        lastScrollMsgId.current = lastMsg.id;

        const timer = setTimeout(() => {
            const isDesignAnnouncement =
                lastMsg?.metadata?.context_type === "design" ||
                Boolean(
                    lastMsg?.attachments &&
                        lastMsg.attachments.some((a) => a.category === "design")
                );
            if (isDesignAnnouncement && lastMsg) {
                scrollToMessage(lastMsg.id, 8, false);
            } else {
                scrollToBottom();
            }
        }, 120);
        return () => clearTimeout(timer);
    }, [orderedMessages, scrollToMessage]);

    const handleSendMessage = async (
        e?: React.SyntheticEvent | false
    ): Promise<void> => {
        if (e) {
            e.preventDefault();
        }
        const text =
            expandAttachmentReferences(
                normalizeComposerBody(body).trim(),
                composerAttachmentRefs
            ) || (pendingFiles.length > 0 ? "\u200b" : "");
        if (!text) {
            return;
        }

        setSending(true);
        const sendAsInternal =
            isIt &&
            (isInternal || Boolean(replyTo && isPrivateMessage(replyTo)));
        try {
            const res = await developmentApi.sendMessage(development.id, {
                content: text,
                is_internal: sendAsInternal,
                type: sendAsInternal ? "internal_note" : "chat",
                reply_to_message_id: replyTo?.id ?? null,
            });
            if (res.result && res.data) {
                for (const file of pendingFiles) {
                    await developmentApi.uploadAttachment(
                        development.id,
                        file,
                        res.data.id,
                        sendAsInternal,
                        "chat"
                    );
                }
                setBody("");
                setComposerAttachmentRefs([]);
                setPendingFiles([]);
                setReplyTo(null);

                const updatedDev = await developmentApi.get(development.id);
                if (updatedDev.result) {
                    onUpdate(updatedDev.data);
                }
            } else {
                showError(
                    res.message || t("developments.detail.message_send_error")
                );
            }
        } catch (err) {
            showError(
                err instanceof Error
                    ? err.message
                    : t("developments.detail.unexpected_error")
            );
        } finally {
            setSending(false);
        }
    };

    const handleFileUpload = async (
        e: React.ChangeEvent<HTMLInputElement>
    ): Promise<void> => {
        const files = Array.from(e.target.files ?? []);
        setPendingFiles((current) => [...current, ...files]);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleComposerPaste = (e: React.ClipboardEvent<HTMLElement>): void => {
        const pastedImages = Array.from(e.clipboardData.items)
            .filter((item) => item.kind === "file" && item.type.startsWith("image/"))
            .map((item) => item.getAsFile())
            .filter((file): file is File => Boolean(file))
            .map((file, index) => {
                if (file.name) {
                    return file;
                }

                const extension = file.type.split("/")[1] || "png";
                return new File(
                    [file],
                    `clipboard-image-${Date.now()}-${index}.${extension}`,
                    { type: file.type }
                );
            });

        if (pastedImages.length === 0) {
            return;
        }

        e.preventDefault();
        setPendingFiles((current) => [...current, ...pastedImages]);
    };

    const openAi = (message?: Message): void => {
        closeComposerFullscreen();
        setAiSource(message ?? null);
        setShowAiModal(true);
    };

    const openGlobalAi = (): void => {
        closeComposerFullscreen();
        setAiSource({
            id: 0,
            content: "",
            created_at: new Date().toISOString(),
        });
        setShowAiModal(true);
    };

    const openAiFromComposer = (content: string): void => {
        if (isComposerEmpty(content)) {
            openGlobalAi();
            return;
        }

        closeComposerFullscreen();
        const cleaned = normalizeComposerBody(content).trim();
        setAiSource({
            id: 0,
            content: cleaned,
            created_at: new Date().toISOString(),
        });
        setShowAiModal(true);
    };

    const aiComposerTooltip = isDesignPhase
        ? isComposerEmpty(body)
            ? t("developments.detail.generate_design_use_phase2")
            : t("developments.detail.generate_design_use_typed_text")
        : isComposerEmpty(body)
          ? t("developments.detail.ask_ai_use_all_chat")
          : t("developments.detail.ask_ai_use_typed_text");

    const conversationText = messages
        .map(
            (message) =>
                `[${formatDate(message.created_at, true)}] ${message.sender_name ?? t("developments.detail.system_sender")}: ${cleanAttachmentReferencesForCopy(message.content)}`
        )
        .join("\n\n");

    const downloadableAttachments = React.useMemo(() => {
        const map = new Map<number, { id: number; name: string }>();

        development.attachments?.forEach((attachment) => {
            map.set(attachment.id, attachment);
        });

        messages.forEach((message) => {
            message.attachments?.forEach((attachment) => {
                map.set(attachment.id, attachment);
            });
        });

        return Array.from(map.values());
    }, [development.attachments, messages]);

    const handleReplyClick = (e: React.MouseEvent, message: Message): void => {
        e.stopPropagation();
        handleReply(message);
    };

    const handleReply = (message: Message): void => {
        setReplyTo(message);
        if (isIt && isPrivateMessage(message)) {
            setIsInternal(true);
        }
    };

    const handleCopyClick = (e: React.MouseEvent, message: Message): void => {
        e.stopPropagation();
        handleCopyContent(message.content, message.id);
    };

    const handleForwardToClient = async (
        e: React.MouseEvent,
        message: Message
    ): Promise<void> => {
        e.stopPropagation();
        try {
            const res = await developmentApi.sendMessage(development.id, {
                content: cleanAttachmentReferencesForCopy(message.content),
                is_internal: false,
                type: "chat",
            });
            if (res.result) {
                const updatedDev = await developmentApi.get(development.id);
                if (updatedDev.result) {
                    onUpdate(updatedDev.data);
                }
                showSuccess(
                    t(
                        "developments.detail.forward_ai_success",
                        "Mensaje enviado al cliente"
                    )
                );
                setTimeout(() => scrollToBottom(), 300);
            } else {
                showError(res.message || "Error");
            }
        } catch (err) {
            showError("Error al enviar mensaje");
        }
    };

    const handleAiClick = (e: React.MouseEvent, message: Message): void => {
        e.stopPropagation();
        openAi(message);
    };

    const handleStartSelection = (
        e: React.MouseEvent,
        messageId: number
    ): void => {
        e.stopPropagation();
        setIsSelectionMode(true);
        setSelectedMessageIds(new Set([messageId]));
    };

    const handleToggleSelectMessage = (messageId: number): void => {
        setSelectedMessageIds((prev) => {
            const next = new Set(prev);
            if (next.has(messageId)) {
                next.delete(messageId);
                if (next.size === 0) {
                    setIsSelectionMode(false);
                }
            } else {
                next.add(messageId);
            }
            return next;
        });
    };

    const handleToggleSelectAll = (): void => {
        if (selectedMessageIds.size === filteredMessages.length) {
            setSelectedMessageIds(new Set());
            setIsSelectionMode(false);
        } else {
            setSelectedMessageIds(new Set(filteredMessages.map((m) => m.id)));
        }
    };

    const handleCancelSelection = (): void => {
        setIsSelectionMode(false);
        setSelectedMessageIds(new Set());
    };

    const handleCopySelected = (): void => {
        const selectedList = filteredMessages.filter((m) =>
            selectedMessageIds.has(m.id)
        );
        if (selectedList.length === 0) {
            return;
        }
        const text = selectedList
            .map(
                (m) =>
                    `[${formatDate(m.created_at, true)}] ${m.sender_name ?? t("developments.detail.user_sender")}: ${cleanAttachmentReferencesForCopy(m.content)}`
            )
            .join("\n\n");
        navigator.clipboard.writeText(text);
        setIsCopiedSelected(true);
        window.setTimeout(() => setIsCopiedSelected(false), 2500);
    };

    const handleAiSelected = (): void => {
        const selectedList = filteredMessages.filter((m) =>
            selectedMessageIds.has(m.id)
        );
        if (selectedList.length === 0) {
            return;
        }
        const text = selectedList
            .map(
                (m) =>
                    `[${formatDate(m.created_at, true)}] ${m.sender_name ?? t("developments.detail.user_sender")}: ${cleanAttachmentReferencesForCopy(m.content)}`
            )
            .join("\n\n");
        closeComposerFullscreen();
        setAiSource({
            id: 0,
            content: text,
            created_at: new Date().toISOString(),
        });
        setShowAiModal(true);
    };

    const renderMessageActions = (message: Message): React.ReactNode => {
        if (isSelectionMode) {
            return null;
        }

        return (
            <>
                {isEditor && (
                    <Tooltip title={t("developments.detail.quote_message")}>
                        <button
                            type="button"
                            onClick={(e) => handleReplyClick(e, message)}
                            className="p-1 rounded-full transition-colors cursor-pointer hover:bg-slate-200/60 dark:hover:bg-white/10 text-slate-500 hover:text-slate-900 dark:text-zinc-400 dark:hover:text-white"
                            aria-label={t("developments.detail.quote_message")}
                        >
                            <Icon value="reply" size={15} />
                        </button>
                    </Tooltip>
                )}

                <Tooltip
                    title={
                        copiedMsgId === message.id
                            ? t("developments.detail.copied_message")
                            : t("developments.detail.copy_message")
                    }
                >
                    <button
                        type="button"
                        onClick={(e) => handleCopyClick(e, message)}
                        className="p-1 rounded-full transition-colors cursor-pointer hover:bg-slate-200/60 dark:hover:bg-white/10 text-slate-500 hover:text-slate-900 dark:text-zinc-400 dark:hover:text-white"
                        aria-label={
                            copiedMsgId === message.id
                                ? t("developments.detail.copied_message")
                                : t("developments.detail.copy_message")
                        }
                    >
                        <Icon
                            value={
                                copiedMsgId === message.id ? "check" : "copy"
                            }
                            size={15}
                            className={
                                copiedMsgId === message.id
                                    ? "text-emerald-500 dark:text-emerald-400"
                                    : ""
                            }
                        />
                    </button>
                </Tooltip>

                {isIt &&
                    (message.type === "ai_response" ||
                        message.type === "ai_design_proposal" ||
                        message.sender_role === "ai") &&
                    message.is_internal && (
                        <Tooltip
                            title={t(
                                "developments.detail.forward_ai_to_client"
                            )}
                        >
                            <button
                                type="button"
                                onClick={(e) =>
                                    handleForwardToClient(e, message)
                                }
                                className="p-1 rounded-full transition-colors cursor-pointer hover:bg-slate-200/60 dark:hover:bg-white/10 text-slate-500 hover:text-slate-900 dark:text-zinc-400 dark:hover:text-white"
                                aria-label={t(
                                    "developments.detail.forward_ai_to_client"
                                )}
                            >
                                <Icon value="send" size={14} />
                            </button>
                        </Tooltip>
                    )}

                {isIt && (
                    <Tooltip title={t("developments.detail.ask_ai_button")}>
                        <button
                            type="button"
                            onClick={(e) => handleAiClick(e, message)}
                            className="p-1 rounded-full transition-colors cursor-pointer hover:bg-slate-200/60 dark:hover:bg-white/10 text-slate-500 hover:text-slate-900 dark:text-zinc-400 dark:hover:text-white"
                            aria-label={t("developments.detail.ask_ai_button")}
                        >
                            <Icon value="sparkles" size={15} />
                        </button>
                    </Tooltip>
                )}

                <Tooltip title={t("developments.detail.expand_message")}>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            expandedMessageButtonRef.current = (
                                e.currentTarget as HTMLButtonElement
                            ).getBoundingClientRect();
                            setExpandedMessage(message);
                        }}
                        className="p-1 rounded-full transition-colors cursor-pointer hover:bg-slate-200/60 dark:hover:bg-white/10 text-slate-500 hover:text-slate-900 dark:text-zinc-400 dark:hover:text-white"
                        aria-label={t("developments.detail.expand_message")}
                    >
                        <Icon value="maximize" size={15} />
                    </button>
                </Tooltip>

                <Tooltip title={t("developments.detail.select_messages")}>
                    <button
                        type="button"
                        onClick={(e) => handleStartSelection(e, message.id)}
                        className="p-1 rounded-full transition-colors cursor-pointer hover:bg-slate-200/60 dark:hover:bg-white/10 text-slate-500 hover:text-slate-900 dark:text-zinc-400 dark:hover:text-white"
                        aria-label={t("developments.detail.select_messages")}
                    >
                        <Icon value="checkCheck" size={15} />
                    </button>
                </Tooltip>

                {message.metadata?.usage && (
                    <Tooltip title={t("ai_modal.stats_title")}>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                setStatsModalMessage(message);
                            }}
                            className="p-1 rounded-full transition-colors cursor-pointer hover:bg-slate-200/60 dark:hover:bg-white/10 text-slate-500 hover:text-slate-900 dark:text-zinc-400 dark:hover:text-white"
                            aria-label={t("ai_modal.stats_title")}
                        >
                            <Icon value="circle-euro" size={15} />
                        </button>
                    </Tooltip>
                )}
            </>
        );
    };

    const renderMessageMetaRow = (
        message: Message,
        align: "left" | "right"
    ): React.ReactNode => (
        <div
            className={`w-full leading-none -mt-0.5 ${align === "right" ? "text-right" : "text-left"}`}
        >
            <div className="relative inline-block leading-none">
                {align === "right" && (
                    <div className="chat-message-actions absolute top-0 right-full mr-1.5 flex items-center gap-0.5 bg-white/95 dark:bg-zinc-800/95 backdrop-blur-xs border border-slate-200 dark:border-zinc-700 shadow-xs rounded-full px-1 py-0.5 z-20">
                        {renderMessageActions(message)}
                    </div>
                )}
                <span className="text-[10px] text-slate-400 leading-none">
                    {date(message.created_at)}
                </span>
                {align === "left" && (
                    <div className="chat-message-actions absolute top-0 left-full ml-1.5 flex items-center gap-0.5 bg-white/95 dark:bg-zinc-800/95 backdrop-blur-xs border border-slate-200 dark:border-zinc-700 shadow-xs rounded-full px-1 py-0.5 z-20">
                        {renderMessageActions(message)}
                    </div>
                )}
            </div>
        </div>
    );

    const handleCopyContent = (text: string, msgId: number): void => {
        navigator.clipboard.writeText(cleanAttachmentReferencesForCopy(text));
        setCopiedMsgId(msgId);
        setTimeout(() => setCopiedMsgId(null), 2500);
    };

    const handlePendingFileClick = (clickedFile: File): void => {
        if (!isImageFile(clickedFile.name, clickedFile.type)) {
            setFileViewerTarget({
                filename: clickedFile.name,
                mimeType: clickedFile.type,
                fileSize: clickedFile.size,
                fileDate: clickedFile.lastModified,
                loadBlob: () => Promise.resolve(clickedFile),
            });
            return;
        }

        const imageFiles = pendingFiles.filter((f) =>
            isImageFile(f.name, f.type)
        );
        const items: LightboxItem[] = imageFiles.map((f) => ({
            url: URL.createObjectURL(f),
            title: f.name,
            isBlob: true,
        }));

        const initialIndex = imageFiles.findIndex((f) => f === clickedFile);
        setLightboxItems(items);
        setLightboxIndex(initialIndex >= 0 ? initialIndex : 0);
        setLightboxId(clickedFile.name);
        setShowLightbox(true);
    };

    const handleAttachmentClick = (
        clickedAttachment: {
            id: number;
            name: string;
            mime_type?: string;
            file_size?: number;
            created_at?: string;
            category?: string;
            file_path?: string;
        },
        messageCreatedAt?: string
    ): void => {
        if (
            clickedAttachment.category === "design" &&
            clickedAttachment.file_path
        ) {
            window.open(clickedAttachment.file_path, "_blank");
            return;
        }

        if (!isImageFile(clickedAttachment.name, clickedAttachment.mime_type)) {
            setFileViewerTarget({
                attachmentId: clickedAttachment.id,
                filename: clickedAttachment.name,
                mimeType: clickedAttachment.mime_type,
                fileSize: clickedAttachment.file_size,
                fileDate: clickedAttachment.created_at || messageCreatedAt,
                loadBlob: () =>
                    developmentApi.getAttachmentBlob(
                        development.id,
                        clickedAttachment.id
                    ),
                directUrl:
                    clickedAttachment.category === "design" &&
                    clickedAttachment.file_path
                        ? clickedAttachment.file_path
                        : undefined,
            });
            return;
        }

        let targetList = allChatImageAttachments;
        let initialIndex = targetList.findIndex(
            (att) => String(att.id) === String(clickedAttachment.id)
        );
        if (initialIndex === -1) {
            targetList = [clickedAttachment, ...targetList];
            initialIndex = 0;
        }

        const items: LightboxItem[] = targetList.map((att) => ({
            id: att.id,
            title: att.name,
            loadBlob: () =>
                developmentApi.getAttachmentBlob(development.id, att.id),
            canDelete: isIt,
        }));

        setLightboxItems(items);
        setLightboxIndex(initialIndex);
        setLightboxId(clickedAttachment.id);
        setShowLightbox(true);
    };

    const deleteAttachmentDirect = async (
        attachmentId: number
    ): Promise<void> => {
        setIsDeletingAttachment(true);

        try {
            const res = await developmentApi.deleteAttachment(
                development.id,
                attachmentId
            );
            if (res.result) {
                setShowLightbox(false);
                setFileViewerTarget(null);
                const refreshed = await developmentApi.get(development.id);
                if (refreshed.result) {
                    onUpdate(refreshed.data);
                }
                showSuccess(t("developments.detail.file_delete_success"));
                return;
            }

            throw new Error(
                res.message || t("developments.detail.file_delete_error")
            );
        } catch (err) {
            showError(
                err instanceof Error
                    ? err.message
                    : t("developments.detail.file_delete_error")
            );
        } finally {
            setIsDeletingAttachment(false);
        }
    };

    ///*<h3 className="text-xs font-bold text-slate-700 dark:text-zinc-200">{t('developments.detail.tab_chat')}</h3>*/
    const chatFeedContent = (
        <div
            id="chat-feed-container"
            ref={chatContainerRef}
            onDragEnter={handleComposerDragEnter}
            onDragOver={handleComposerDragOver}
            onDragLeave={handleComposerDragLeave}
            onDrop={handleComposerDrop}
            className={`bg-box border border-slate-200/80 shadow-xs flex flex-col overflow-hidden scroll-m-8 ${
                isFullscreen
                    ? "fixed inset-0 z-[99990] h-screen w-screen rounded-none bg-slate-50 dark:bg-dark-box"
                    : "relative flex-1 h-[78vh] min-h-[520px] sm:min-h-[640px] lg:min-h-[700px] max-h-[880px] rounded-2xl"
            }`}
        >
            {!isTextareaFullscreen && renderComposerDropOverlay()}
            {/* Chat Feed Header */}
            <div
                onClick={scrollToTop}
                className={`relative border-b transition-all cursor-pointer select-none group shrink-0 ${
                    isFullscreen
                        ? "px-5 pt-1 pb-0 bg-primary text-white border-primary-950/20 shadow-sm"
                        : "px-4 pt-1.5 pb-1 bg-slate-50 dark:bg-dark-subtle border-slate-200 dark:border-white/10"
                }`}
                title={t("developments.detail.scroll_chat_top")}
            >
                {isFullscreen && (
                    <div className="text-center">
                        {development.code && (
                            <span className="text-xs font-bold px-2.5 py-0.5 rounded-md bg-white/20 text-white shrink-0 tracking-wider">
                                {development.code}
                            </span>
                        )}
                        <span className="text-sm font-semibold text-white truncate max-w-[320px] sm:max-w-[480px] md:max-w-[640px]">
                            {development.title}
                        </span>
                    </div>
                )}
                <div className="flex items-center justify-between">
                    <div className="flex items-center justify-start gap-2 min-w-0">
                        <Button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowDownloadModal(true);
                            }}
                            variant="custom"
                            size="none"
                            tooltip={t(
                                "developments.detail.download_conversation"
                            )}
                            className={`p-1.5 ${
                                isFullscreen
                                    ? "text-white/80 hover:text-white"
                                    : "text-slate-500 hover:text-primary dark:text-zinc-500 dark:hover:text-primary-400"
                            }`}
                            icon={<Icon value="download" size={18} />}
                        />
                    </div>
                    <div className="flex flex-1 items-center justify-center gap-2 min-w-0">
                        {isIt && (isDesignPhase || isRequirementsPhase) ? (
                            <Button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openGlobalAi();
                                }}
                                variant="custom"
                                size="none"
                                tooltip={t("developments.detail.ask_ai_global")}
                                className={`p-1.5 ${
                                    isFullscreen
                                        ? "text-white/80 hover:text-white"
                                        : "text-slate-500 hover:text-primary dark:text-zinc-500 dark:hover:text-primary-400"
                                }`}
                                icon={<Icon value="sparkles" size={18} />}
                            />
                        ) : (
                            <></>
                        )}
                        <Button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowFilters((prev) => !prev);
                            }}
                            variant="custom"
                            size="none"
                            tooltip={
                                showFilters
                                    ? t("filters.hide")
                                    : t("filters.show")
                            }
                            className={`p-1.5 ${
                                isFullscreen
                                    ? "text-white/80 hover:text-white"
                                    : "text-slate-500 hover:text-primary dark:text-zinc-500 dark:hover:text-primary-400"
                            }`}
                            icon={
                                <Icon
                                    value="filter"
                                    size={17}
                                    fill={
                                        hasActiveFilters
                                            ? "currentColor"
                                            : "none"
                                    }
                                    className={
                                        isFullscreen
                                            ? hasActiveFilters
                                                ? "text-white fill-white hover:opacity-80"
                                                : "text-white/80 hover:text-white"
                                            : hasActiveFilters
                                              ? "text-primary dark:text-primary-400 fill-primary dark:fill-primary-400 hover:opacity-80"
                                              : showFilters
                                                ? "text-primary dark:text-primary-400 hover:opacity-80"
                                                : "text-slate-500 hover:text-primary dark:text-zinc-500 dark:hover:text-primary-400"
                                    }
                                />
                            }
                        />
                    </div>
                    <div className="flex items-center justify-end gap-1.5">
                        <Button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                const next = !isFullscreen;
                                setIsFullscreen(next);
                                if (!next) {
                                    (
                                        document.activeElement as HTMLElement | null
                                    )?.blur();
                                }
                            }}
                            variant="custom"
                            size="none"
                            tooltip={
                                isFullscreen
                                    ? t("developments.detail.minimize")
                                    : t("developments.detail.fullscreen")
                            }
                            className={`p-1.5 cursor-pointer transition-colors bg-transparent shadow-none rounded-lg ${
                                isFullscreen
                                    ? "text-white/90 hover:text-white"
                                    : "text-slate-600 hover:text-primary dark:text-zinc-400 dark:hover:text-primary-400"
                            }`}
                            icon={
                                <Icon
                                    value={
                                        isFullscreen ? "minimize" : "maximize"
                                    }
                                    size={18}
                                />
                            }
                        />
                    </div>
                </div>
            </div>

            {/* Search & Filter Accordion */}
            <div
                className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out shrink-0 ${
                    showFilters
                        ? "grid-rows-[1fr] opacity-100"
                        : "grid-rows-[0fr] opacity-0 pointer-events-none"
                }`}
            >
                <div className="overflow-hidden">
                    {/* Search & Filter Toolbar */}
                    <div className="relative z-30 px-4 py-2.5 bg-white dark:bg-dark-box border-b border-slate-200 dark:border-white/10 flex flex-col gap-2 select-none shadow-2xs">
                        <div className="flex items-center gap-2 overflow-x-auto pb-0.5 flex-nowrap">
                            {/* Search Input */}
                            <div className="flex-1 min-w-[140px] shrink">
                                <SearchInput
                                    value={searchQuery}
                                    onChange={setSearchQuery}
                                    placeholder={t(
                                        "developments.detail.search_messages_placeholder"
                                    )}
                                />
                            </div>

                            <Select
                                value={typeFilter}
                                onChange={setTypeFilter}
                                size="sm"
                                className="shrink-0 w-auto [&_.select-trigger]:h-9"
                                placeholder={t("filters.message_type")}
                                options={[
                                    ...(
                                        (isIt
                                            ? ["all", "it", "ai", "internal"]
                                            : ["all", "it"]) as const
                                    ).map((type) => ({
                                        value: type,
                                        label: t(
                                            `developments.detail.filter_${type === "it" ? "our" : type}`
                                        ),
                                        icon: TYPE_FILTER_ICONS[
                                            type as keyof typeof TYPE_FILTER_ICONS
                                        ],
                                        iconPosition: "left" as const,
                                    })),
                                    ...clientSenders.map((name) => ({
                                        value: `client:${name}`,
                                        label: t(
                                            "developments.detail.filter_client_name",
                                            { name }
                                        ),
                                        icon: "user",
                                        iconPosition: "left" as const,
                                    })),
                                ]}
                            />

                            {/* Date range button → opens modal */}
                            <button
                                type="button"
                                onClick={() => {
                                    setPendingRange({
                                        start: startDate || null,
                                        end: endDate || null,
                                    });
                                    setShowDatePickerModal(true);
                                }}
                                className={`shrink-0 h-9 px-2.5 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer border ${
                                    startDate || endDate
                                        ? "bg-primary text-white border-primary shadow-xs"
                                        : "bg-white dark:bg-dark-box text-slate-600 dark:text-zinc-300 border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-zinc-800"
                                }`}
                                title={t("developments.detail.filter_dates")}
                            >
                                <Icon value="calendar" size={14} />
                                <span className="hidden sm:inline">
                                    {t("developments.detail.filter_dates")}
                                </span>
                                {(startDate || endDate) && (
                                    <span className="w-2 h-2 rounded-full bg-amber-300 animate-pulse" />
                                )}
                            </button>
                        </div>

                        {/* Active filter chips */}
                        {hasActiveFilters && (
                            <div className="flex items-center gap-1.5 flex-wrap pt-1">
                                <span className="text-[10px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-wider mr-0.5">
                                    {t("filters.active")}:
                                </span>

                                {typeFilter !== "all" && (
                                    <Badge variant="primary" size="sm">
                                        {typeFilter === "it" && (
                                            <Icon
                                                value="usershield"
                                                size={10}
                                                className="text-white"
                                            />
                                        )}
                                        {typeFilter === "ai" && (
                                            <Icon
                                                value="sparkles"
                                                size={10}
                                                className="text-white"
                                            />
                                        )}
                                        {typeFilter === "internal" && (
                                            <Icon
                                                value="lock"
                                                size={10}
                                                className="text-white"
                                            />
                                        )}
                                        {typeFilter.startsWith("client:") && (
                                            <Icon
                                                value="user"
                                                size={10}
                                                className="text-white"
                                            />
                                        )}

                                        {typeFilter.startsWith("client:")
                                            ? t(
                                                  "developments.detail.filter_client_name",
                                                  {
                                                      name: typeFilter.substring(
                                                          7
                                                      ),
                                                  }
                                              )
                                            : t(
                                                  `developments.detail.filter_${typeFilter === "it" ? "our" : typeFilter}`
                                              )}

                                        <button
                                            type="button"
                                            onClick={() => setTypeFilter("all")}
                                            aria-label={t("filters.clear")}
                                            className="ml-0.5 rounded-full p-1 text-white hover:bg-white/20 cursor-pointer"
                                        >
                                            <Icon value="x" size={10} />
                                        </button>
                                    </Badge>
                                )}

                                {(startDate || endDate) && (
                                    <Badge variant="primary" size="sm">
                                        <Icon
                                            value="calendar"
                                            size={10}
                                            className="text-white"
                                        />
                                        {startDate && (
                                            <span>
                                                {formatDate(startDate, false)}
                                            </span>
                                        )}
                                        {startDate && endDate && <span>→</span>}
                                        {endDate && (
                                            <span>
                                                {formatDate(endDate, false)}
                                            </span>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setStartDate("");
                                                setEndDate("");
                                            }}
                                            aria-label={t("filters.clear")}
                                            className="ml-0.5 rounded-full p-0.5 text-white hover:bg-white/20 cursor-pointer"
                                        >
                                            <Icon value="x" size={10} />
                                        </button>
                                    </Badge>
                                )}

                                {searchQuery.trim() && (
                                    <Badge variant="primary" size="sm">
                                        <Icon
                                            value="search"
                                            size={10}
                                            className="text-white"
                                        />
                                        "{searchQuery}"
                                        <button
                                            type="button"
                                            onClick={() => setSearchQuery("")}
                                            aria-label={t("filters.clear")}
                                            className="ml-0.5 rounded-full p-0.5 text-white hover:bg-white/20 cursor-pointer"
                                        >
                                            <Icon value="x" size={10} />
                                        </button>
                                    </Badge>
                                )}

                                <button
                                    type="button"
                                    onClick={handleResetFilters}
                                    className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30 cursor-pointer transition-colors"
                                >
                                    <Icon value="rotateCcw" size={10} />
                                    {t("filters.clear")}
                                </button>

                                <span className="text-[11px] text-slate-400 dark:text-zinc-500 ml-1">
                                    {t("developments.detail.filtered_results", {
                                        filtered: filteredMessages.length,
                                        total: orderedMessages.length,
                                    })}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Messages Scroll Area */}
            <div
                ref={messagesContainerRef}
                className={`flex-1 min-h-0 overflow-y-auto space-y-6 bg-slate-50/40 dark:bg-black/20 ${
                    isFullscreen
                        ? "px-4 sm:px-6 md:px-8 py-4"
                        : "px-2.5 py-2.5 sm:px-3 sm:py-3"
                }`}
                onClick={handleMessageContentClick}
            >
                <div className="w-full flex flex-col space-y-6">
                    {orderedMessages.length === 0 && (
                        <div className="text-center py-12 text-slate-400 text-xs">
                            {t("developments.detail.chat_empty")}
                        </div>
                    )}

                    {orderedMessages.length > 0 &&
                        filteredMessages.length === 0 && (
                            <div className="text-center py-12 flex flex-col items-center gap-2">
                                <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-zinc-800 flex items-center justify-center text-slate-400">
                                    <Icon value="search" size={22} />
                                </div>
                                <p className="text-xs text-slate-500 dark:text-zinc-400 font-medium">
                                    {t("developments.detail.no_messages_found")}
                                </p>
                                <button
                                    type="button"
                                    onClick={handleResetFilters}
                                    className="btn secondary text-xs h-8 px-3 mt-1 cursor-pointer"
                                >
                                    <Icon value="rotateCcw" size={13} />
                                    <span>{t("filters.clear")}</span>
                                </button>
                            </div>
                        )}

                    {filteredMessages.map((msg) => {
                        const isSystem =
                            msg.type === "system" || msg.type === "step_event";
                        const isAi =
                            msg.type === "ai_response" ||
                            msg.type === "ai_design_proposal";
                        const isInternalNote = msg.is_internal && !isAi;
                        const isMsgIt =
                            !isAi &&
                            !isSystem &&
                            (msg.sender_role === "it" ||
                                Boolean(msg.is_internal));
                        const isMsgClient = !isAi && !isSystem && !isMsgIt;
                        const isMine = isViewerIt ? isMsgIt : isMsgClient;

                        if (isSystem) {
                            const isSelected = selectedMessageIds.has(msg.id);
                            const stepTitle = msg.metadata?.step_tag
                                ? t(
                                      "developments.detail.timeline.steps." +
                                          msg.metadata.step_tag
                                  )
                                : msg.metadata?.step_title || msg.content;
                            const targetStepTitle = msg.metadata
                                ?.target_step_tag
                                ? t(
                                      "developments.detail.timeline.steps." +
                                          msg.metadata.target_step_tag
                                  )
                                : msg.metadata?.target_step_title;
                            const formattedDate = date(msg.created_at);

                            // Find next step
                            const actualStepTag =
                                msg.metadata?._original_step_tag ||
                                msg.metadata?.step_tag;
                            const currentStepIndex =
                                development.steps?.findIndex(
                                    (s) =>
                                        (s.tag || s.step_key) === actualStepTag
                                ) ?? -1;
                            const nextStep = (() => {
                                if (
                                    !development.steps ||
                                    currentStepIndex < 0
                                ) {
                                    return null;
                                }

                                for (
                                    let i = currentStepIndex + 1;
                                    i < development.steps.length;
                                    i++
                                ) {
                                    const candidate = development.steps[i];
                                    const tag =
                                        candidate.tag ||
                                        candidate.step_key ||
                                        "";
                                    if (
                                        jumpDesign &&
                                        (tag === "design" || tag === "Diseño")
                                    ) {
                                        continue;
                                    }

                                    if (
                                        jumpTasks &&
                                        (tag === "tasks" || tag === "Tareas")
                                    ) {
                                        continue;
                                    }

                                    return candidate;
                                }

                                return null;
                            })();

                            const isReopenEvent =
                                msg.metadata?.auto === "step_reopened";

                            return (
                                <div
                                    id={`message-${msg.id}`}
                                    key={msg.id}
                                    data-step-key={msg.metadata?.step_key}
                                    onClick={() => {
                                        if (isSelectionMode) {
                                            handleToggleSelectMessage(msg.id);
                                        }
                                    }}
                                    className={`animate-message-entrance flex items-center justify-center gap-2 my-4 transition-all ${
                                        isSelectionMode
                                            ? "cursor-pointer select-none hover:opacity-90"
                                            : ""
                                    }`}
                                >
                                    {isSelectionMode && (
                                        <div
                                            className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 transition-all ${
                                                isSelected
                                                    ? "bg-primary border-2 border-primary text-white shadow-xs scale-105"
                                                    : "border-2 border-slate-300 dark:border-zinc-600 bg-white dark:bg-zinc-800"
                                            }`}
                                        >
                                            {isSelected && (
                                                <Icon
                                                    value="check"
                                                    size={11}
                                                    strokeWidth={3}
                                                />
                                            )}
                                        </div>
                                    )}
                                    <div className="flex flex-col items-center gap-2">
                                        <div
                                            className={`${
                                                isReopenEvent
                                                    ? "bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800/80 text-rose-800 dark:text-rose-300"
                                                    : "bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/80 text-emerald-800 dark:text-emerald-300"
                                            } text-xs px-4 py-2 rounded-xl font-medium shadow-xs transition-all duration-300 ${
                                                isSelected
                                                    ? "ring-2 ring-primary dark:ring-primary-400 shadow-md"
                                                    : ""
                                            } ${
                                                highlightedMsgId === msg.id
                                                    ? "app-highlight-flash"
                                                    : ""
                                            }`}
                                        >
                                            <span className="flex justify-center opacity-75 text-[10px]">
                                                {formattedDate}
                                            </span>
                                            <div className="flex items-center justify-center gap-2">
                                                <Icon
                                                    value={
                                                        isReopenEvent
                                                            ? "undo"
                                                            : "checkCircle"
                                                    }
                                                    size={14}
                                                    className={
                                                        isReopenEvent
                                                            ? "text-rose-600 dark:text-rose-400"
                                                            : "text-emerald-600 dark:text-emerald-400"
                                                    }
                                                />
                                                <span>
                                                    {t(
                                                        isReopenEvent
                                                            ? "developments.detail.timeline.phase_reopened"
                                                            : "developments.detail.timeline.phase_completed"
                                                    )}
                                                    :{" "}
                                                    <strong>{stepTitle}</strong>
                                                </span>
                                            </div>
                                        </div>

                                        {!isReopenEvent && nextStep && (
                                            <div
                                                className={`bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800/60 text-blue-800 dark:text-blue-300 text-xs px-4 py-2 rounded-xl font-medium shadow-xs transition-all duration-300 ${
                                                    isSelected
                                                        ? "ring-2 ring-primary dark:ring-primary-400 shadow-md"
                                                        : ""
                                                } ${
                                                    highlightedMsgId === msg.id
                                                        ? "app-highlight-flash"
                                                        : ""
                                                }`}
                                            >
                                                <span className="flex justify-center opacity-75 text-[10px]">
                                                    {formattedDate}
                                                </span>
                                                <div className="flex items-center justify-center gap-2">
                                                    <Icon
                                                        value="play"
                                                        size={12}
                                                        className="text-blue-600 dark:text-blue-400"
                                                    />
                                                    <span>
                                                        {t(
                                                            "developments.detail.timeline.phase_started"
                                                        )}
                                                        :{" "}
                                                        <strong>
                                                            {nextStep.tag
                                                                ? t(
                                                                      "developments.detail.timeline.steps." +
                                                                          nextStep.tag
                                                                  )
                                                                : nextStep.title}
                                                        </strong>
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                        {isReopenEvent && targetStepTitle && (
                                            <div
                                                className={`bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800/60 text-blue-800 dark:text-blue-300 text-xs px-4 py-2 rounded-xl font-medium shadow-xs transition-all duration-300 ${
                                                    isSelected
                                                        ? "ring-2 ring-primary dark:ring-primary-400 shadow-md"
                                                        : ""
                                                } ${
                                                    highlightedMsgId === msg.id
                                                        ? "app-highlight-flash"
                                                        : ""
                                                }`}
                                            >
                                                <span className="flex justify-center opacity-75 text-[10px]">
                                                    {formattedDate}
                                                </span>
                                                <div className="flex items-center justify-center gap-2">
                                                    <Icon
                                                        value="play"
                                                        size={12}
                                                        className="text-blue-600 dark:text-blue-400"
                                                    />
                                                    <span>
                                                        {t(
                                                            "developments.detail.timeline.phase_reopened_target"
                                                        )}
                                                        :{" "}
                                                        <strong>
                                                            {targetStepTitle}
                                                        </strong>
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        }

                        if (isAi) {
                            const isSelected = selectedMessageIds.has(msg.id);
                            const isAiClient =
                                msg.metadata?.ai_target_audience === "client" ||
                                (!msg.is_internal &&
                                    msg.metadata?.context_type === "design") ||
                                !msg.is_internal;
                            const isDesignAnnouncement =
                                msg.metadata?.context_type === "design" ||
                                Boolean(
                                    msg.attachments &&
                                        msg.attachments.some(
                                            (a) => a.category === "design"
                                        )
                                );
                            const designAttachment = msg.attachments?.find(
                                (a) =>
                                    a.category === "design" ||
                                    a.name.toLowerCase().endsWith(".html")
                            );

                            return (
                                <div
                                    id={`message-${msg.id}`}
                                    key={msg.id}
                                    onClick={() => {
                                        if (isSelectionMode) {
                                            handleToggleSelectMessage(msg.id);
                                        }
                                    }}
                                    className={`animate-message-entrance flex items-start gap-2 justify-end transition-all ${isFullscreen ? "w-full" : ""} ${
                                        isSelectionMode
                                            ? "cursor-pointer select-none hover:opacity-90"
                                            : ""
                                    }`}
                                >
                                    <div
                                        className={`group chat-message-group flex min-w-[220px] flex-col gap-0 items-end ${isFullscreen ? "w-full max-w-full" : "max-w-[calc(100%-2rem)] sm:max-w-[92%] lg:max-w-3xl"}`}
                                    >
                                        <div className="flex items-center gap-1.5 px-1 mb-0.5 flex-row-reverse">
                                            {isDesignAnnouncement ? (
                                                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-purple-700 dark:text-purple-300">
                                                    <Icon
                                                        value="palette"
                                                        size={13}
                                                    />
                                                    <span>
                                                        {t(
                                                            "developments.detail.design_title"
                                                        )}
                                                    </span>
                                                </span>
                                            ) : isAiClient ? (
                                                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-sky-700 dark:text-sky-400">
                                                    <Icon
                                                        value="bot"
                                                        size={13}
                                                    />
                                                    <span>
                                                        {t(
                                                            "developments.detail.ai_target_client"
                                                        )}
                                                    </span>
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-purple-700 dark:text-purple-300">
                                                    {/*<Icon value="lock" size={12} className="text-amber-600 dark:text-amber-400" />*/}
                                                    <Icon
                                                        value="bot"
                                                        size={13}
                                                    />
                                                    <span>
                                                        {t(
                                                            "developments.detail.ai_target_it"
                                                        )}
                                                    </span>
                                                </span>
                                            )}
                                        </div>

                                        <div
                                            className={`relative group w-full min-w-[220px] overflow-hidden break-words rounded-2xl rounded-tr-none py-2 px-3 sm:py-2.5 sm:px-4 text-xs shadow-xs leading-relaxed transition-all duration-300 ${
                                                isAiClient &&
                                                !isDesignAnnouncement
                                                    ? "bg-sky-50/70 dark:bg-sky-950/25 border border-sky-200 dark:border-sky-800/60 text-slate-900 dark:text-zinc-100"
                                                    : "bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800/60 text-purple-900 dark:text-purple-100"
                                            } ${
                                                isSelected
                                                    ? "ring-2 ring-primary dark:ring-primary-400 shadow-md"
                                                    : ""
                                            } ${
                                                highlightedMsgId === msg.id
                                                    ? "app-highlight-flash"
                                                    : ""
                                            }`}
                                        >
                                            {msg.reply_to && (
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        scrollToMessage(
                                                            msg.reply_to?.id ??
                                                                0
                                                        )
                                                    }
                                                    className="block w-full text-left border-l-2 border-primary pl-2 mb-1.5 text-[11px] truncate cursor-pointer bg-white/50 dark:bg-black/20 p-1 rounded-r text-slate-600 dark:text-zinc-300 hover:opacity-80 transition-opacity"
                                                >
                                                    <strong>
                                                        {
                                                            msg.reply_to
                                                                .sender_name
                                                        }
                                                    </strong>
                                                    : {msg.reply_to.content}
                                                </button>
                                            )}

                                            {!isViewerIt &&
                                            isDesignAnnouncement ? (
                                                <div className="text-sm font-medium mb-1">
                                                    {t(
                                                        "developments.detail.design_proposal_ready"
                                                    )}
                                                </div>
                                            ) : (
                                                <CollapsibleChatMessage
                                                    content={msg.content}
                                                    msgId={msg.id}
                                                    variant={
                                                        isAiClient &&
                                                        !isDesignAnnouncement
                                                            ? "ai_client"
                                                            : "ai"
                                                    }
                                                />
                                            )}

                                            {msg.attachments &&
                                                msg.attachments.length > 0 && (
                                                    <div
                                                        className={`mt-2 flex flex-col gap-1 border-t pt-2 ${isAiClient && !isDesignAnnouncement ? "border-sky-200 dark:border-sky-800/40" : "border-purple-300/60"}`}
                                                    >
                                                        {msg.attachments.map(
                                                            (attachment) => {
                                                                const isDesign =
                                                                    isDesignAnnouncement &&
                                                                    attachment.id ===
                                                                        designAttachment?.id;

                                                                if (
                                                                    !isViewerIt &&
                                                                    isDesignAnnouncement &&
                                                                    !isDesign
                                                                ) {
                                                                    return null;
                                                                }

                                                                if (isDesign) {
                                                                    const match =
                                                                        attachment.name.match(
                                                                            /(?:[-_\s]|^)v(\d+)(?=[^a-z0-9]|$)/i
                                                                        );
                                                                    let versionNumber =
                                                                        match
                                                                            ? parseInt(
                                                                                  match[1],
                                                                                  10
                                                                              )
                                                                            : null;
                                                                    if (
                                                                        versionNumber ===
                                                                        null
                                                                    ) {
                                                                        const allDesigns =
                                                                            (
                                                                                development.attachments ??
                                                                                []
                                                                            ).filter(
                                                                                (
                                                                                    a: Attachment
                                                                                ) =>
                                                                                    a.category?.toLowerCase() ===
                                                                                        "design" ||
                                                                                    a.name
                                                                                        .toLowerCase()
                                                                                        .endsWith(
                                                                                            ".html"
                                                                                        )
                                                                            );
                                                                        const sorted =
                                                                            [
                                                                                ...allDesigns,
                                                                            ].sort(
                                                                                (
                                                                                    a,
                                                                                    b
                                                                                ) => {
                                                                                    const timeA =
                                                                                        a.created_at
                                                                                            ? new Date(
                                                                                                  a.created_at
                                                                                              ).getTime()
                                                                                            : a.id;
                                                                                    const timeB =
                                                                                        b.created_at
                                                                                            ? new Date(
                                                                                                  b.created_at
                                                                                              ).getTime()
                                                                                            : b.id;
                                                                                    return (
                                                                                        timeA -
                                                                                        timeB
                                                                                    );
                                                                                }
                                                                            );
                                                                        const idx =
                                                                            sorted.findIndex(
                                                                                (
                                                                                    item
                                                                                ) =>
                                                                                    item.id ===
                                                                                    attachment.id
                                                                            );
                                                                        if (
                                                                            idx !==
                                                                            -1
                                                                        ) {
                                                                            versionNumber =
                                                                                idx +
                                                                                1;
                                                                        } else {
                                                                            versionNumber = 1;
                                                                        }
                                                                    }
                                                                    const dateLabel =
                                                                        formatDate(
                                                                            attachment.created_at ||
                                                                                msg.created_at,
                                                                            true,
                                                                            true
                                                                        );
                                                                    const sizeLabel =
                                                                        formatFileSize(
                                                                            attachment.file_size
                                                                        );

                                                                    return (
                                                                        <div
                                                                            key={
                                                                                attachment.id
                                                                            }
                                                                            onClick={() =>
                                                                                handleAttachmentClick(
                                                                                    attachment,
                                                                                    msg.created_at
                                                                                )
                                                                            }
                                                                            className="cursor-pointer rounded-xl bg-purple-100/70 dark:bg-purple-950/50 border border-purple-200 dark:border-purple-800/60 hover:bg-purple-200/50 transition-colors mt-1 overflow-hidden"
                                                                        >
                                                                            {versionNumber !==
                                                                                null && (
                                                                                <div className="w-full text-center py-1 px-3 text-[11px] font-bold bg-purple-200/80 text-purple-900 dark:bg-purple-900/60 dark:text-purple-200 border-b border-purple-300/60 dark:border-purple-800/60 tracking-wide select-none">
                                                                                    {t(
                                                                                        "developments.detail.version_label",
                                                                                        {
                                                                                            version:
                                                                                                versionNumber,
                                                                                        }
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                            <div className="flex items-center gap-2 min-w-0 p-2.5">
                                                                                <span className="p-1.5 rounded-lg bg-purple-600 text-white shrink-0">
                                                                                    <Icon
                                                                                        value="palette"
                                                                                        size={
                                                                                            24
                                                                                        }
                                                                                    />
                                                                                </span>
                                                                                <div className="min-w-0 flex-1 text-left">
                                                                                    <p className="text-xs font-bold text-purple-950 dark:text-purple-200 truncate">
                                                                                        {
                                                                                            attachment.name
                                                                                        }
                                                                                    </p>
                                                                                    {(dateLabel ||
                                                                                        sizeLabel) && (
                                                                                        <p className="text-xs font-semibold text-purple-900 dark:text-purple-200 truncate flex items-center gap-1.5 mt-0.5">
                                                                                            <Icon
                                                                                                value="clock"
                                                                                                size={
                                                                                                    13
                                                                                                }
                                                                                                className="shrink-0 text-purple-700 dark:text-purple-300"
                                                                                            />
                                                                                            {dateLabel && (
                                                                                                <span>
                                                                                                    {
                                                                                                        dateLabel
                                                                                                    }
                                                                                                </span>
                                                                                            )}
                                                                                            {dateLabel &&
                                                                                                sizeLabel && (
                                                                                                    <span className="text-purple-400 dark:text-purple-500">
                                                                                                        •
                                                                                                    </span>
                                                                                                )}
                                                                                            {sizeLabel && (
                                                                                                <span className="text-purple-800/80 dark:text-purple-300 font-normal text-[11px]">
                                                                                                    {
                                                                                                        sizeLabel
                                                                                                    }
                                                                                                </span>
                                                                                            )}
                                                                                        </p>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                }

                                                                return (
                                                                    <AttachmentThumbnail
                                                                        key={
                                                                            attachment.id
                                                                        }
                                                                        name={
                                                                            attachment.name
                                                                        }
                                                                        mimeType={
                                                                            attachment.mime_type
                                                                        }
                                                                        fileSize={
                                                                            attachment.file_size
                                                                        }
                                                                        overlayImageName
                                                                        load={() =>
                                                                            developmentApi.getAttachmentBlob(
                                                                                development.id,
                                                                                attachment.id
                                                                            )
                                                                        }
                                                                        onClick={() =>
                                                                            handleAttachmentClick(
                                                                                attachment,
                                                                                msg.created_at
                                                                            )
                                                                        }
                                                                    />
                                                                );
                                                            }
                                                        )}
                                                    </div>
                                                )}

                                            <MessageGestures
                                                onReply={() => handleReply(msg)}
                                                disabled={isSelectionMode}
                                            />
                                        </div>

                                        {renderMessageMetaRow(msg, "right")}
                                    </div>

                                    {isSelectionMode && (
                                        <div className="pt-4 shrink-0">
                                            <div
                                                className={`w-5 h-5 rounded-full flex items-center justify-center transition-all ${
                                                    isSelected
                                                        ? "bg-primary border-2 border-primary text-white shadow-xs scale-105"
                                                        : "border-2 border-slate-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 hover:border-primary"
                                                }`}
                                            >
                                                {isSelected && (
                                                    <Icon
                                                        value="check"
                                                        size={11}
                                                        strokeWidth={3}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        }

                        if (isInternalNote) {
                            const isSelected = selectedMessageIds.has(msg.id);
                            return (
                                <div
                                    id={`message-${msg.id}`}
                                    key={msg.id}
                                    onClick={() => {
                                        if (isSelectionMode) {
                                            handleToggleSelectMessage(msg.id);
                                        }
                                    }}
                                    className={`animate-message-entrance flex items-start gap-2 justify-end transition-all ${isFullscreen ? "w-full" : ""} ${
                                        isSelectionMode
                                            ? "cursor-pointer select-none hover:opacity-90"
                                            : ""
                                    }`}
                                >
                                    <div
                                        className={`group chat-message-group flex min-w-[220px] flex-col gap-0 items-end ${isFullscreen ? "w-full max-w-full" : "max-w-[88%] sm:max-w-[85%] lg:max-w-3xl"}`}
                                    >
                                        <div className="flex items-center gap-1.5 w-full px-1 mb-0.5 justify-end">
                                            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-slate-700 dark:text-zinc-300 leading-none">
                                                {msg.sender_name}
                                                <Icon
                                                    value="usershield"
                                                    size={13}
                                                    className="shrink-0"
                                                />
                                                <Icon
                                                    value="lock"
                                                    size={14}
                                                    className="text-amber-600 dark:text-amber-400 shrink-0"
                                                />
                                            </span>
                                        </div>

                                        <div
                                            className={`relative group w-full rounded-2xl rounded-tr-none py-2 px-3 sm:py-2 sm:px-3.5 text-xs shadow-xs whitespace-pre-wrap leading-relaxed min-w-[220px] bg-amber-50/70 dark:bg-amber-950/25 border border-amber-300/90 dark:border-amber-700/60 text-slate-900 dark:text-zinc-100 transition-all duration-300 ${
                                                isSelected
                                                    ? "ring-2 ring-primary dark:ring-primary-400 shadow-md"
                                                    : ""
                                            } ${
                                                highlightedMsgId === msg.id
                                                    ? "app-highlight-flash"
                                                    : ""
                                            }`}
                                        >
                                            {msg.reply_to && (
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        scrollToMessage(
                                                            msg.reply_to?.id ??
                                                                0
                                                        )
                                                    }
                                                    className="block w-full text-left border-l-2 border-amber-500 pl-2 mb-1.5 text-[11px] truncate cursor-pointer bg-white/60 dark:bg-black/20 p-1 rounded-r text-slate-700 dark:text-zinc-300 hover:opacity-80 transition-opacity"
                                                >
                                                    <strong>
                                                        {
                                                            msg.reply_to
                                                                .sender_name
                                                        }
                                                    </strong>
                                                    : {msg.reply_to.content}
                                                </button>
                                            )}

                                            <CollapsibleChatMessage
                                                content={msg.content}
                                                msgId={msg.id}
                                                variant="internal"
                                            />

                                            {msg.attachments &&
                                                msg.attachments.length > 0 && (
                                                    <div className="mt-2 flex flex-col gap-1 border-t border-amber-300/60 pt-2">
                                                        {msg.attachments.map(
                                                            (attachment) => (
                                                                <AttachmentThumbnail
                                                                    key={
                                                                        attachment.id
                                                                    }
                                                                    name={
                                                                        attachment.name
                                                                    }
                                                                    mimeType={
                                                                        attachment.mime_type
                                                                    }
                                                                    fileSize={
                                                                        attachment.file_size
                                                                    }
                                                                    overlayImageName
                                                                    load={() =>
                                                                        developmentApi.getAttachmentBlob(
                                                                            development.id,
                                                                            attachment.id
                                                                        )
                                                                    }
                                                                    onClick={() =>
                                                                        handleAttachmentClick(
                                                                            attachment,
                                                                            msg.created_at
                                                                        )
                                                                    }
                                                                />
                                                            )
                                                        )}
                                                    </div>
                                                )}

                                            <MessageGestures
                                                onReply={() => handleReply(msg)}
                                                disabled={isSelectionMode}
                                            />
                                        </div>

                                        {renderMessageMetaRow(msg, "right")}
                                    </div>

                                    {isSelectionMode && (
                                        <div className="pt-4 shrink-0">
                                            <div
                                                className={`w-5 h-5 rounded-full flex items-center justify-center transition-all ${
                                                    isSelected
                                                        ? "bg-primary border-2 border-primary text-white shadow-xs scale-105"
                                                        : "border-2 border-slate-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 hover:border-primary"
                                                }`}
                                            >
                                                {isSelected && (
                                                    <Icon
                                                        value="check"
                                                        size={11}
                                                        strokeWidth={3}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        }

                        const isSelected = selectedMessageIds.has(msg.id);
                        return (
                            <div
                                id={`message-${msg.id}`}
                                key={msg.id}
                                onClick={() => {
                                    if (isSelectionMode) {
                                        handleToggleSelectMessage(msg.id);
                                    }
                                }}
                                className={`animate-message-entrance flex items-start gap-2 ${!isMine ? "justify-start" : "justify-end"} transition-all ${isFullscreen ? "w-full" : ""} ${
                                    isSelectionMode
                                        ? "cursor-pointer select-none hover:opacity-90"
                                        : ""
                                }`}
                            >
                                {isSelectionMode && !isMine && (
                                    <div className="pt-4 shrink-0">
                                        <div
                                            className={`w-5 h-5 rounded-full flex items-center justify-center transition-all ${
                                                isSelected
                                                    ? "bg-primary border-2 border-primary text-white shadow-xs scale-105"
                                                    : "border-2 border-slate-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 hover:border-primary"
                                            }`}
                                        >
                                            {isSelected && (
                                                <Icon
                                                    value="check"
                                                    size={11}
                                                    strokeWidth={3}
                                                />
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div
                                    className={`group chat-message-group flex min-w-[220px] flex-col gap-0 ${!isMine ? "items-start" : "items-end"} ${isFullscreen ? "w-full max-w-full" : "max-w-[88%] sm:max-w-[85%] lg:max-w-3xl"}`}
                                >
                                    <div
                                        className={`flex items-center justify-between w-full px-1 ${!isMine ? "flex-row" : "flex-row-reverse"}`}
                                    >
                                        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-slate-700 dark:text-zinc-300">
                                            {!isMine && (
                                                <Icon
                                                    value={
                                                        isMsgClient
                                                            ? "user"
                                                            : "usershield"
                                                    }
                                                    size={13}
                                                    className="shrink-0"
                                                />
                                            )}
                                            {msg.sender_name}
                                            {isMine && (
                                                <Icon
                                                    value={
                                                        isMsgClient
                                                            ? "user"
                                                            : "usershield"
                                                    }
                                                    size={13}
                                                    className="shrink-0"
                                                />
                                            )}
                                        </span>
                                    </div>
                                    <div
                                        className={`relative group w-full rounded-2xl py-2 px-3 sm:py-2 sm:px-3.5 text-xs shadow-xs whitespace-pre-wrap leading-relaxed min-w-[220px] transition-all duration-300 ${
                                            isMine
                                                ? "bg-blue-100 border border-blue-300 text-slate-900 dark:bg-blue-950/50 dark:border-blue-800/60 dark:text-zinc-100 rounded-tr-none"
                                                : "border border-slate-300 dark:border-zinc-700 text-slate-900 dark:text-zinc-100 bg-transparent rounded-tl-none"
                                        } ${
                                            isSelected
                                                ? "ring-2 ring-primary dark:ring-primary-400 shadow-md"
                                                : ""
                                        } ${
                                            highlightedMsgId === msg.id
                                                ? "app-highlight-flash"
                                                : ""
                                        }`}
                                    >
                                        {msg.reply_to && (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    scrollToMessage(
                                                        msg.reply_to?.id ?? 0
                                                    )
                                                }
                                                className={`block w-full text-left border-l-2 pl-2 mb-1.5 text-[11px] truncate cursor-pointer hover:opacity-80 transition-opacity ${
                                                    isMine
                                                        ? "border-blue-500 text-slate-700 dark:text-zinc-300 bg-blue-200/50 dark:bg-blue-900/30 p-1 rounded-r"
                                                        : "border-slate-400 text-slate-600 dark:text-zinc-300 bg-slate-100/70 dark:bg-white/5 p-1 rounded-r"
                                                }`}
                                            >
                                                <strong>
                                                    {msg.reply_to.sender_name}
                                                </strong>
                                                : {msg.reply_to.content}
                                            </button>
                                        )}

                                        <CollapsibleChatMessage
                                            content={msg.content}
                                            msgId={msg.id}
                                            variant={isMine ? "mine" : "other"}
                                        />

                                        {msg.attachments &&
                                            msg.attachments.length > 0 && (
                                                <div
                                                    className={`mt-2 flex flex-col gap-1 border-t pt-2 ${isMine ? "border-blue-200 dark:border-blue-800/50" : "border-slate-200"}`}
                                                >
                                                    {msg.attachments.map(
                                                        (attachment) => (
                                                            <AttachmentThumbnail
                                                                key={
                                                                    attachment.id
                                                                }
                                                                name={
                                                                    attachment.name
                                                                }
                                                                mimeType={
                                                                    attachment.mime_type
                                                                }
                                                                fileSize={
                                                                    attachment.file_size
                                                                }
                                                                overlayImageName
                                                                load={() =>
                                                                    developmentApi.getAttachmentBlob(
                                                                        development.id,
                                                                        attachment.id
                                                                    )
                                                                }
                                                                onClick={() =>
                                                                    handleAttachmentClick(
                                                                        attachment,
                                                                        msg.created_at
                                                                    )
                                                                }
                                                                inverted={false}
                                                            />
                                                        )
                                                    )}
                                                </div>
                                            )}

                                        <MessageGestures
                                            onReply={() => handleReply(msg)}
                                            disabled={isSelectionMode}
                                        />
                                    </div>
                                    {renderMessageMetaRow(
                                        msg,
                                        !isMine ? "left" : "right"
                                    )}
                                </div>

                                {isSelectionMode && isMine && (
                                    <div className="pt-6 shrink-0">
                                        <div
                                            className={`w-5 h-5 rounded-full flex items-center justify-center transition-all ${
                                                isSelected
                                                    ? "bg-primary border-2 border-primary text-white shadow-xs scale-105"
                                                    : "border-2 border-slate-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 hover:border-primary"
                                            }`}
                                        >
                                            {isSelected && (
                                                <Icon
                                                    value="check"
                                                    size={11}
                                                    strokeWidth={3}
                                                />
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    <div ref={messagesEndRef} />
                </div>
            </div>

            {/* Multi-selection Action Bar */}
            {isSelectionMode && (
                <div className="p-3 bg-white/95 dark:bg-dark-box/95 backdrop-blur-md border-t border-slate-200 dark:border-white/10 shadow-lg flex items-center justify-between gap-3 animate-fade-in z-30 select-none">
                    <div className="flex items-center gap-2.5">
                        <span className="text-xs font-bold bg-primary/10 text-primary px-2.5 py-1 rounded-lg">
                            {t("developments.detail.selected_count", {
                                count: selectedMessageIds.size,
                            })}
                        </span>
                        <button
                            type="button"
                            onClick={handleToggleSelectAll}
                            className="text-xs font-semibold text-slate-600 hover:text-primary dark:text-zinc-400 dark:hover:text-primary cursor-pointer hover:underline"
                        >
                            {selectedMessageIds.size === filteredMessages.length
                                ? t("developments.detail.deselect_all")
                                : t("developments.detail.select_all")}
                        </button>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={handleCopySelected}
                            disabled={selectedMessageIds.size === 0}
                            className={`btn btn-secondary h-8 px-3 text-xs flex items-center gap-1.5 ${
                                selectedMessageIds.size === 0
                                    ? "opacity-40 cursor-not-allowed pointer-events-none"
                                    : "cursor-pointer"
                            }`}
                            title={t("developments.detail.copy_selected")}
                        >
                            <Icon
                                value={isCopiedSelected ? "check" : "copy"}
                                size={14}
                                className={
                                    isCopiedSelected ? "text-emerald-500" : ""
                                }
                            />
                            <span>
                                {isCopiedSelected
                                    ? t("developments.detail.copied_selected")
                                    : t("developments.detail.copy_selected")}
                            </span>
                        </button>

                        {isIt && (
                            <button
                                type="button"
                                onClick={handleAiSelected}
                                disabled={selectedMessageIds.size === 0}
                                className={`btn bg-ai-gradient h-8 px-3 text-xs flex items-center gap-1.5 ${
                                    selectedMessageIds.size === 0
                                        ? "opacity-40 cursor-not-allowed pointer-events-none"
                                        : "cursor-pointer"
                                }`}
                                title={t("developments.detail.ai_selected")}
                            >
                                <Icon
                                    value="sparkles"
                                    size={13}
                                    className="text-yellow-300"
                                />
                                <span>
                                    {t("developments.detail.ai_selected")}
                                </span>
                            </button>
                        )}

                        <button
                            type="button"
                            onClick={handleCancelSelection}
                            className="p-1.5 text-slate-500 hover:text-slate-900 dark:text-zinc-400 dark:hover:text-white rounded-lg hover:bg-slate-100 dark:hover:bg-zinc-800 cursor-pointer transition-colors ml-1"
                            title={t("developments.detail.cancel_selection")}
                        >
                            <Icon value="x" size={18} />
                        </button>
                    </div>
                </div>
            )}

            {/* Message Composer */}
            {!isEditor ? (
                <div
                    className={`p-4 border-t border-slate-200 text-center text-xs text-slate-500 italic bg-slate-50 ${isFullscreen ? "" : "rounded-b-2xl"}`}
                >
                    {isCancelled
                        ? t("developments.detail.cancelled_mode_notice")
                        : t("developments.detail.viewer_mode_notice")}
                </div>
            ) : (
                <div
                    className={`relative shrink-0 border-t border-slate-200 dark:border-white/10 ${isFullscreen ? "" : "rounded-b-2xl overflow-hidden"}`}
                    onDragEnter={handleComposerDragEnter}
                    onDragOver={handleComposerDragOver}
                    onDragLeave={handleComposerDragLeave}
                    onDrop={handleComposerDrop}
                    onPaste={handleComposerPaste}
                >
                    <form
                        ref={composerContainerRef}
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleSendMessage();
                        }}
                        className={`relative py-2.5 space-y-3 w-full ${isFullscreen ? "px-4 sm:px-6 md:px-8" : "px-4"}`}
                    >
                        <div className="relative grid grid-cols-3 items-center min-h-[28px] text-xs">
                            <div className="flex items-center justify-start">
                                {renderCompletePhaseButton()}
                            </div>
                            <div className="flex items-center justify-center">
                                {renderComposerModeToggle()}
                            </div>
                            <div className="flex items-center justify-end">
                                <Button
                                    type="button"
                                    onClick={openComposerFullscreen}
                                    variant="custom"
                                    size="none"
                                    tooltip={t(
                                        "developments.detail.expand_composer"
                                    )}
                                    className="px-3 py-2 text-slate-500 hover:text-primary dark:text-zinc-400 dark:hover:text-primary-400 rounded-lg bg-transparent shadow-none"
                                    icon={<Icon value="maximize" size={20} />}
                                />
                            </div>
                        </div>

                        {replyTo && isIt && isPrivateMessage(replyTo) && (
                            <Alert variant="warning">
                                {t("developments.detail.quote_private_warning")}
                            </Alert>
                        )}

                        {replyTo && (
                            <div
                                className={`flex items-center justify-between gap-2 px-3 py-2 text-xs border-l-2 ${isInternal ? "bg-amber-50/80 border-amber-500" : "bg-slate-50 border-slate-300"}`}
                            >
                                <span className="truncate">
                                    <strong>{replyTo.sender_name}</strong>:{" "}
                                    {cleanAttachmentReferencesForCopy(
                                        replyTo.content
                                    )}
                                </span>
                                <button
                                    type="button"
                                    onClick={clearReplyTo}
                                    className="cursor-pointer"
                                >
                                    <Icon value="x" size={13} />
                                </button>
                            </div>
                        )}

                        {pendingFiles.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                                {pendingFiles.map((file, index) => (
                                    <span
                                        key={`${file.name}-${index}`}
                                        className={`relative inline-flex items-center rounded-xl bg-primary text-white text-xs font-medium shadow-xs shrink-0 select-none overflow-hidden ${pendingFilePreviewUrls.has(file) ? "h-20 w-32" : "h-9"}`}
                                    >
                                        <button
                                            type="button"
                                            onClick={() =>
                                                handlePendingFileClick(file)
                                            }
                                            className={`relative inline-flex items-center gap-1.5 h-full cursor-pointer hover:bg-white/10 transition-colors min-w-0 text-white ${pendingFilePreviewUrls.has(file) ? "w-full justify-center p-0" : "max-w-[220px] px-3"}`}
                                            title={file.name}
                                        >
                                            {pendingFilePreviewUrls.has(file) ? (
                                                <img
                                                    src={pendingFilePreviewUrls.get(file)}
                                                    alt=""
                                                    className="absolute inset-0 h-full w-full rounded-xl object-cover"
                                                />
                                            ) : (
                                                <Icon value="paperclip" size={13} className="shrink-0" />
                                            )}
                                            <span className={`truncate ${pendingFilePreviewUrls.has(file) ? "absolute inset-x-0 bottom-0 bg-black/65 px-2 py-1 text-center text-[10px] text-white" : ""}`}>
                                                {file.name}
                                            </span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setPendingFiles((files) =>
                                                    files.filter(
                                                        (_, fileIndex) =>
                                                            fileIndex !== index
                                                    )
                                                )
                                            }
                                            className={`cursor-pointer transition-colors text-white/90 hover:text-white shrink-0 ${pendingFilePreviewUrls.has(file) ? "absolute right-0 top-0 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-black/55 p-1" : "h-full px-2 hover:bg-white/20"}`}
                                            title={t(
                                                "developments.detail.remove_file"
                                            )}
                                        >
                                            <Icon value="x" size={14} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}

                        <div className="chat-composer-row">
                            <input
                                type="file"
                                multiple
                                ref={fileInputRef}
                                onChange={handleFileUpload}
                                className="hidden"
                            />

                            <div
                                className={`overflow-hidden transition-all duration-300 flex-shrink-0 flex items-center justify-center ${isRecordingAudio ? "w-0 opacity-0" : "w-10 opacity-100"}`}
                            >
                                <Tooltip
                                    title={t(
                                        "developments.detail.attach_files"
                                    )}
                                >
                                    <button
                                        type="button"
                                        onClick={() =>
                                            fileInputRef.current?.click()
                                        }
                                        title={t(
                                            "developments.detail.attach_button"
                                        )}
                                        className="btn secondary chat-composer-btn w-9 shrink-0"
                                    >
                                        <Icon value="paperclip" size={18} />
                                    </button>
                                </Tooltip>
                            </div>

                            <MicrophoneButton
                                onListeningChange={setIsRecordingAudio}
                                onTranscriptChange={(text) => {
                                    setBaseBodyForRecording((prev) => {
                                        const base =
                                            prev !== null ? prev : body;
                                        setBody(
                                            base + (text ? " " + text : "")
                                        );
                                        return base;
                                    });
                                }}
                                onCancel={() => {
                                    setBaseBodyForRecording((prev) => {
                                        if (prev !== null) {
                                            setBody(prev);
                                        }
                                        return null;
                                    });
                                }}
                                onResult={(text) => {
                                    setBaseBodyForRecording((prev) => {
                                        const base =
                                            prev !== null ? prev : body;
                                        const cleanedPrev =
                                            base
                                                .replace(/<[^>]+>/g, "")
                                                .trim() === ""
                                                ? ""
                                                : base;
                                        setBody(
                                            cleanedPrev
                                                ? `${cleanedPrev} ${text}`
                                                : text
                                        );
                                        return null;
                                    });
                                }}
                                className="btn secondary chat-composer-btn !p-0"
                                iconSize={18}
                            />

                            <div className="chat-composer-field-wrap">
                                {hasRichTextMarkup(body) ? (
                                    <div
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                            if (
                                                ENTER_SEND &&
                                                e.key === "Enter" &&
                                                !e.shiftKey
                                            ) {
                                                e.preventDefault();
                                                handleSendMessage(e);
                                            } else if (
                                                e.key === "Backspace" ||
                                                e.key === "Delete"
                                            ) {
                                                e.preventDefault();
                                                setBody("");
                                            }
                                        }}
                                        className={`chat-composer-field chat-composer-field--rendered ${isInternal ? "chat-composer-field--internal" : "chat-composer-field--public"} group flex items-center justify-between gap-2`}
                                    >
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const plainText =
                                                    html2text(body);
                                                setBody(plainText);
                                            }}
                                            title={t(
                                                "developments.detail.clear_formatting"
                                            )}
                                            className="shrink-0 p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:text-zinc-500 dark:hover:text-zinc-300 hover:bg-slate-200/50 dark:hover:bg-white/10 transition-colors cursor-pointer"
                                        >
                                            <Icon value="eraser" size={16} />
                                        </button>

                                        <div
                                            className="flex-1 min-w-0 overflow-y-auto max-h-full select-text"
                                            onClick={openComposerFullscreen}
                                        >
                                            <Markdown>{body}</Markdown>
                                        </div>

                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openComposerFullscreen();
                                            }}
                                            title={t("common.fullscreen")}
                                            className="shrink-0 p-1.5 rounded-lg text-slate-400 hover:text-primary dark:text-zinc-500 dark:hover:text-primary-400 hover:bg-slate-200/50 dark:hover:bg-white/10 transition-colors cursor-pointer"
                                        >
                                            <Icon value="maximize" size={16} />
                                        </button>
                                    </div>
                                ) : (
                                    <textarea
                                        ref={composerTextareaRef}
                                        rows={1}
                                        value={body}
                                        onChange={handleTextareaChange}
                                        onKeyDown={(e) => {
                                            if (
                                                ENTER_SEND &&
                                                e.key === "Enter" &&
                                                !e.shiftKey
                                            ) {
                                                e.preventDefault();
                                                handleSendMessage(e);
                                            }
                                        }}
                                        placeholder={
                                            isInternal
                                                ? t(
                                                      "developments.detail.internal_placeholder"
                                                  )
                                                : t(
                                                      "developments.detail.composer_placeholder"
                                                  )
                                        }
                                        className={`chat-composer-field ${isInternal ? "chat-composer-field--internal" : "chat-composer-field--public"}`}
                                    />
                                )}
                            </div>

                            {isIt && (isDesignPhase || isRequirementsPhase) && (
                                <Tooltip title={aiComposerTooltip}>
                                    <button
                                        type="button"
                                        onClick={() => openAiFromComposer(body)}
                                        aria-label={aiComposerTooltip}
                                        className="btn secondary chat-composer-btn"
                                    >
                                        <Icon
                                            value={
                                                isDesignPhase
                                                    ? "palette"
                                                    : "sparkles"
                                            }
                                            size={17}
                                        />
                                    </button>
                                </Tooltip>
                            )}

                            <button
                                type="submit"
                                disabled={
                                    sending ||
                                    (isComposerEmpty(body) &&
                                        pendingFiles.length === 0)
                                }
                                title={
                                    isInternal
                                        ? t(
                                              "developments.detail.send_internal_note"
                                          )
                                        : t("developments.detail.send_message")
                                }
                                className={`btn chat-composer-btn ${isInternal ? "warning" : "primary"}`}
                            >
                                <Icon value="sendHorizontal" size={18} />
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <DatePickerModal
                isOpen={showDatePickerModal}
                onClose={() => setShowDatePickerModal(false)}
                range={pendingRange}
                onRangeChange={setPendingRange}
                onApply={() => {
                    setStartDate(pendingRange.start ?? "");
                    setEndDate(pendingRange.end ?? "");
                }}
                onClear={() => {
                    setStartDate("");
                    setEndDate("");
                }}
            />

            <CompletePhaseModal
                isOpen={showStepSignModal}
                onClose={() => {
                    setShowStepSignModal(false);
                    setIsSigningStep(false);
                }}
                step={signableCurrentStep?.step ?? null}
                steps={workflowSteps}
                developmentCreatedAt={development.created_at}
                onConfirm={(shouldGenerateDoc, createTask) => {
                    void handleConfirmSignStep(shouldGenerateDoc, createTask);
                }}
                isSubmitting={isSigningStep}
                requirementDocsCount={requirementDocsCount}
                existingTasksCount={development.tasks?.length ?? 0}
                onGenerateDocument={handleGenerateRequirements}
                isGeneratingDocument={isGeneratingDoc}
                designDocsCount={designDocsCount}
                onOpenDesignModal={() => setShowAiModal(true)}
            />

            {/* AI Stats Modal */}
            <Modal
                isOpen={Boolean(statsModalMessage)}
                onClose={() => setStatsModalMessage(null)}
                title={t("ai_modal.stats_title")}
                maxWidth="md"
                cancelLabel={t("ai_modal.close")}
            >
                {statsModalMessage?.metadata?.usage ? (
                    (() => {
                        const usage = statsModalMessage.metadata.usage;
                        const inTokens =
                            usage.input_tokens ||
                            usage.prompt_tokens ||
                            usage.promptTokenCount ||
                            usage.prompt_token_count ||
                            0;
                        const outTokens =
                            usage.output_tokens ||
                            usage.completion_tokens ||
                            usage.candidatesTokenCount ||
                            usage.candidates_token_count ||
                            0;
                        const totalTokens =
                            usage.total_tokens ||
                            usage.totalTokenCount ||
                            usage.total_token_count ||
                            inTokens + outTokens ||
                            0;
                        const model =
                            statsModalMessage.metadata.model || "Unknown";
                        const provider =
                            statsModalMessage.metadata.provider || "Unknown";
                        const cost = statsModalMessage.metadata.cost || 0;

                        return (
                            <div className="space-y-4 mb-4 mt-2">
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    <div className="p-3 bg-white dark:bg-[#121214] rounded-lg border border-slate-200 dark:border-white/5 shadow-xs flex flex-col items-center justify-center text-center">
                                        <Icon
                                            value="globe"
                                            size={18}
                                            className="mb-2 text-slate-400 dark:text-zinc-500"
                                        />
                                        <div className="text-[10px] font-bold text-slate-500 dark:text-zinc-400 mb-1 uppercase min-h-[20px] block">
                                            {t("ai_modal.stats_provider")}
                                        </div>
                                        <div
                                            className="font-semibold text-slate-800 dark:text-zinc-200 capitalize truncate w-full"
                                            title={provider}
                                        >
                                            {provider}
                                        </div>
                                    </div>
                                    <div className="p-3 bg-white dark:bg-[#121214] rounded-lg border border-slate-200 dark:border-white/5 shadow-xs flex flex-col items-center justify-center text-center">
                                        <Icon
                                            value="bot"
                                            size={18}
                                            className="mb-2 text-slate-400 dark:text-zinc-500"
                                        />
                                        <div className="text-[10px] font-bold text-slate-500 dark:text-zinc-400 mb-1 uppercase min-h-[20px] block">
                                            {t("ai_modal.stats_model")}
                                        </div>
                                        <div
                                            className="font-semibold text-slate-800 dark:text-zinc-200 capitalize truncate w-full"
                                            title={model}
                                        >
                                            {model}
                                        </div>
                                    </div>
                                    <div className="p-3 bg-white dark:bg-[#121214] rounded-lg border border-emerald-500/20 bg-emerald-50 shadow-xs dark:bg-emerald-950/20 flex flex-col items-center justify-center text-center">
                                        <Icon
                                            value="circle-euro"
                                            size={18}
                                            className="mb-2 text-emerald-500 dark:text-emerald-400"
                                        />
                                        <div className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 mb-1 uppercase font-semibold min-h-[20px] block">
                                            {t("ai_modal.stats_cost")}
                                        </div>
                                        <div
                                            className="font-bold text-emerald-700 dark:text-emerald-300 truncate w-full"
                                            title={`${cost}€`}
                                        >
                                            {price(cost, "euro", "0.00000€", 5)}
                                        </div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 gap-4">
                                    <div className="p-3 bg-white dark:bg-[#121214] rounded-lg border border-slate-200 dark:border-white/5 shadow-xs flex flex-col items-center justify-center text-center">
                                        <Icon
                                            value="arrowUp"
                                            size={18}
                                            className="mb-2 text-slate-400 dark:text-zinc-500"
                                        />
                                        <div className="text-[10px] font-bold text-slate-500 dark:text-zinc-400 mb-1 uppercase min-h-[32px] block">
                                            {t("ai_modal.stats_in_tokens")}
                                        </div>
                                        <div className="font-bold text-slate-800 dark:text-zinc-200 text-lg">
                                            {quantity(inTokens)}
                                        </div>
                                    </div>
                                    <div className="p-3 bg-white dark:bg-[#121214] rounded-lg border border-slate-200 dark:border-white/5 shadow-xs flex flex-col items-center justify-center text-center">
                                        <Icon
                                            value="arrowDown"
                                            size={18}
                                            className="mb-2 text-slate-400 dark:text-zinc-500"
                                        />
                                        <div className="text-[10px] font-bold text-slate-500 dark:text-zinc-400 mb-1 uppercase min-h-[32px] block">
                                            {t("ai_modal.stats_out_tokens")}
                                        </div>
                                        <div className="font-bold text-slate-800 dark:text-zinc-200 text-lg">
                                            {quantity(outTokens)}
                                        </div>
                                    </div>
                                    <div className="p-3 bg-white dark:bg-[#121214] rounded-lg border border-primary/30 bg-primary/5 shadow-xs flex flex-col items-center justify-center text-center">
                                        <Icon
                                            value="coins"
                                            size={18}
                                            className="mb-2 text-primary-500 dark:text-primary-400"
                                        />
                                        <div className="text-[10px] font-bold text-primary-600 dark:text-primary-400 mb-1 uppercase font-semibold min-h-[32px] block">
                                            {t("ai_modal.stats_total_tokens")}
                                        </div>
                                        <div className="font-bold text-primary-700 dark:text-primary-300 text-lg">
                                            {quantity(totalTokens)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })()
                ) : (
                    <div className="p-4 text-center text-slate-500 mb-4 mt-2">
                        {t("ai_modal.stats_nodata")}
                    </div>
                )}
            </Modal>

            {/* AI Assistant Modal */}
            <AskAiModal
                isOpen={showAiModal}
                onClose={() => setShowAiModal(false)}
                development={development}
                isDesignMode={isDesignPhase}
                initialRequirementDocId={prefilledRequirementDocId}
                onMessageAdded={async (newMsg: unknown) => {
                    const updatedDev = await developmentApi.get(development.id);
                    if (updatedDev.result) {
                        onUpdate(updatedDev.data);
                    }
                    setTimeout(() => {
                        if (
                            newMsg &&
                            typeof newMsg === "object" &&
                            "id" in newMsg &&
                            typeof (newMsg as { id: unknown }).id === "number"
                        ) {
                            scrollToMessage(
                                (newMsg as { id: number }).id,
                                8,
                                false
                            );
                        } else {
                            scrollToBottom();
                        }
                    }, 350);
                }}
                onAttachmentsUpdated={async () => {
                    const updatedDev = await developmentApi.get(development.id);
                    if (updatedDev.result) {
                        onUpdate(updatedDev.data);
                    }
                }}
                initialPrompt={
                    aiSource?.type === "ai_response"
                        ? (aiSource.metadata?.prompt ?? "")
                        : aiSource?.content
                }
                initialResponse={
                    aiSource?.type === "ai_response"
                        ? aiSource.content
                        : undefined
                }
                replyToMessageId={aiSource?.id ? aiSource.id : null}
                attachments={aiSource?.attachments}
                conversationText={conversationText}
            />
            <DownloadModal
                isOpen={showDownloadModal}
                onClose={() => setShowDownloadModal(false)}
                title={t("developments.detail.download_conversation")}
                suffix={`${development.code ?? development.id}_${t("download_modal.chat_suffix")}`}
                content={conversationText}
                formats={["md", "txt", "pdf", "html"]}
                includeFilesOption
                attachments={downloadableAttachments}
                loadAttachmentBlob={(attachmentId) =>
                    developmentApi.getAttachmentBlob(
                        development.id,
                        attachmentId
                    )
                }
                pdfMeta={
                    {
                        title:
                            development.title ?? t("developments.development"),
                        code: development.code,
                        fields: [
                            ...(development.code
                                ? [
                                      {
                                          label: t("developments.code"),
                                          value: development.code,
                                      },
                                  ]
                                : []),
                            ...(development.status
                                ? [
                                      {
                                          label: t("developments.status"),
                                          value: development.status,
                                      },
                                  ]
                                : []),
                            ...(development.priority
                                ? [
                                      {
                                          label: t("developments.priority"),
                                          value: development.priority,
                                      },
                                  ]
                                : []),
                            ...(development.client?.name
                                ? [
                                      {
                                          label: t("developments.client_label"),
                                          value: development.client.name,
                                      },
                                  ]
                                : []),
                            ...(development.assigned?.name
                                ? [
                                      {
                                          label: t("developments.assigned_to"),
                                          value: development.assigned.name,
                                      },
                                  ]
                                : []),
                            ...(development.created_at
                                ? [
                                      {
                                          label: t("developments.created_at"),
                                          value: formatDate(
                                              development.created_at,
                                              false
                                          ),
                                      },
                                  ]
                                : []),
                        ],
                    } satisfies PdfMeta
                }
            />

            {/* Image Fullscreen Lightbox Backdrop */}
            <ImageLightboxModal
                isOpen={showLightbox}
                items={lightboxItems}
                initialIndex={lightboxIndex}
                initialId={lightboxId}
                onClose={() => setShowLightbox(false)}
                onDelete={
                    isIt
                        ? (item) => {
                              const attId =
                                  typeof item.id === "number"
                                      ? item.id
                                      : Number(item.id);
                              if (attId) {
                                  void deleteAttachmentDirect(attId);
                              }
                          }
                        : undefined
                }
            />

            <FileViewer
                isOpen={Boolean(fileViewerTarget)}
                onClose={() => setFileViewerTarget(null)}
                title={fileViewerTarget?.filename ?? ""}
                filename={fileViewerTarget?.filename ?? ""}
                mimeType={fileViewerTarget?.mimeType}
                fileSize={fileViewerTarget?.fileSize}
                fileDate={fileViewerTarget?.fileDate}
                directUrl={fileViewerTarget?.directUrl}
                loadBlob={
                    fileViewerTarget?.loadBlob ??
                    (() => Promise.reject(new Error("Missing file")))
                }
                allowDownload={true}
            />

            {expandedMessage && (
                <Modal
                    isOpen={Boolean(expandedMessage)}
                    onClose={() => setExpandedMessage(null)}
                    title={t("developments.detail.expanded_message_title")}
                    maxWidth="3xl"
                    cancelLabel={t("modal.close")}
                    getOriginRect={() => expandedMessageButtonRef.current}
                    bodyClassName="flex flex-col p-6"
                >
                    <div className="flex flex-col flex-1 min-h-0 gap-4">
                        {!isClient && (
                            <div className="flex justify-center">
                                <Badge
                                    variant={
                                        expandedMessage.is_internal
                                            ? "warning"
                                            : "success"
                                    }
                                    icon={
                                        expandedMessage.is_internal
                                            ? "lock"
                                            : "unlock"
                                    }
                                    size="lg"
                                >
                                    {expandedMessage.is_internal
                                        ? t("developments.detail.mode_internal")
                                        : t("developments.detail.mode_public")}
                                </Badge>
                            </div>
                        )}

                        <div className="flex flex-col items-center justify-center text-center">
                            <span className="font-semibold text-slate-800 dark:text-zinc-200">
                                {expandedMessage.user?.name ||
                                    expandedMessage.sender_name ||
                                    t("developments.detail.role_system")}
                            </span>
                            <span className="text-sm text-slate-500 dark:text-zinc-400">
                                {formatDate(expandedMessage.created_at, true)}
                            </span>
                        </div>

                        {/* Message content box with copy button top-right */}
                        <div className="relative flex-1 min-h-0 p-4 bg-slate-50 dark:bg-dark-box rounded-xl border border-slate-200 dark:border-white/10 overflow-y-auto">
                            <button
                                type="button"
                                onClick={() =>
                                    handleCopyContent(
                                        expandedMessage.content,
                                        expandedMessage.id
                                    )
                                }
                                className={`absolute top-2 right-2 z-10 p-1.5 rounded-lg transition-all cursor-pointer ${
                                    copiedMsgId === expandedMessage.id
                                        ? "text-emerald-600 dark:text-emerald-400"
                                        : "text-slate-400 hover:text-primary dark:text-zinc-500 dark:hover:text-primary-400"
                                }`}
                                title={
                                    copiedMsgId === expandedMessage.id
                                        ? t(
                                              "developments.detail.copied_message"
                                          )
                                        : t("developments.detail.copy_message")
                                }
                            >
                                <Icon
                                    value={
                                        copiedMsgId === expandedMessage.id
                                            ? "check"
                                            : "copy"
                                    }
                                    size={18}
                                />
                            </button>
                            <Markdown className="text-sm leading-relaxed text-slate-800 dark:text-zinc-200 select-text pr-8">
                                {expandedMessage.content}
                            </Markdown>
                        </div>

                        {expandedMessage.attachments &&
                            expandedMessage.attachments.length > 0 && (
                                <div className="space-y-2">
                                    <p className="font-bold text-slate-700 dark:text-zinc-300 text-center">
                                        {t(
                                            "developments.detail.attachments_title"
                                        )}{" "}
                                        ({expandedMessage.attachments.length})
                                    </p>
                                    <div className="flex flex-col gap-1.5">
                                        {expandedMessage.attachments.map(
                                            (att) => (
                                                <AttachmentThumbnail
                                                    key={att.id}
                                                    name={att.name}
                                                    mimeType={att.mime_type}
                                                    fileSize={att.file_size}
                                                    load={() =>
                                                        developmentApi.getAttachmentBlob(
                                                            development.id,
                                                            att.id
                                                        )
                                                    }
                                                    onClick={() =>
                                                        handleAttachmentClick(
                                                            att,
                                                            expandedMessage.created_at
                                                        )
                                                    }
                                                />
                                            )
                                        )}
                                    </div>
                                </div>
                            )}
                    </div>
                </Modal>
            )}

            {/* Fullscreen Rich Text Editor Modal */}
            {isTextareaFullscreen &&
                typeof document !== "undefined" &&
                createPortal(
                    <div
                        ref={fullscreenComposerRef}
                        className="fixed inset-0 z-[110000] flex flex-col bg-white dark:bg-dark-box overflow-hidden"
                        onDragEnter={handleComposerDragEnter}
                        onDragOver={handleComposerDragOver}
                        onDragLeave={handleComposerDragLeave}
                        onDrop={handleComposerDrop}
                        onPaste={handleComposerPaste}
                    >
                        {/* Header */}
                        <div
                            className={`grid grid-cols-3 items-center px-6 pt-2 pb-1 border-b shrink-0 ${
                                isInternal
                                    ? "bg-amber-600 text-white border-amber-700"
                                    : "bg-primary text-white border-primary-900"
                            }`}
                        >
                            <div className="flex items-center justify-start">
                                {hasRichTextMarkup(body) && (
                                    <Button
                                        type="button"
                                        onClick={() => {
                                            const plainText = html2text(body);
                                            setBody(plainText);
                                            closeComposerFullscreen();
                                        }}
                                        size="sm"
                                        variant="primaryinverted"
                                        className="py-2.5!"
                                    >
                                        <Icon value="eraser" size={14} />
                                        {t(
                                            "developments.detail.clear_formatting"
                                        )}
                                    </Button>
                                )}
                            </div>
                            <div className="flex items-center justify-center">
                                {renderComposerModeToggle("text-xs shadow-2xs")}
                            </div>
                            <div className="flex items-center justify-end">
                                <Button
                                    type="button"
                                    onClick={closeComposerFullscreen}
                                    variant="custom"
                                    size="none"
                                    tooltip={t("developments.detail.minimize")}
                                    className="hover:opacity-80 p-1 rounded-lg text-white transition-colors cursor-pointer bg-transparent shadow-none"
                                    icon={<Icon value="minimize" size={18} />}
                                />
                            </div>
                        </div>

                        {/* Rich Editor Area */}
                        <div className="relative flex-1 p-5 overflow-hidden flex flex-col bg-slate-50/50 dark:bg-black/20 gap-3">
                            {replyTo && isIt && isPrivateMessage(replyTo) && (
                                <Alert variant="warning">
                                    {t(
                                        "developments.detail.quote_private_warning"
                                    )}
                                </Alert>
                            )}

                            {replyTo && (
                                <div
                                    className={`flex items-center justify-between gap-2 px-3 py-2 text-xs border-l-2 rounded-r-lg bg-white dark:bg-dark-box shadow-xs ${isInternal ? "border-amber-500" : "border-slate-300"}`}
                                >
                                    <span className="truncate">
                                        <strong>{replyTo.sender_name}</strong>:{" "}
                                        {cleanAttachmentReferencesForCopy(
                                            replyTo.content
                                        )}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={clearReplyTo}
                                        className="cursor-pointer text-slate-400 hover:text-slate-700"
                                    >
                                        <Icon value="x" size={13} />
                                    </button>
                                </div>
                            )}

                            <div className="relative flex-1 min-h-0 flex flex-col">
                                <RichText
                                    value={body}
                                    onChange={setBody}
                                    placeholder={
                                        isInternal
                                            ? t(
                                                  "developments.detail.internal_rich_placeholder"
                                              )
                                            : t(
                                                  "developments.detail.public_rich_placeholder"
                                              )
                                    }
                                    disabled={sending}
                                    minHeight="350px"
                                    className="flex-1 shadow-xs"
                                    autoFocus
                                    rounded
                                />
                            </div>

                            {/* Attachments */}
                            <div className="w-full shrink-0 px-3 pt-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <button
                                        type="button"
                                        onClick={() =>
                                            fileInputRef.current?.click()
                                        }
                                        className="h-9 px-3 text-xs font-semibold rounded-xl border border-slate-200 dark:border-white/20 hover:bg-slate-100 dark:hover:bg-white/10 flex items-center gap-1.5 cursor-pointer shrink-0 bg-white dark:bg-dark-box shadow-2xs"
                                    >
                                        <Icon value="paperclip" size={14} />
                                        <span>
                                            {t(
                                                "developments.detail.attach_files"
                                            )}
                                        </span>
                                    </button>

                                    {pendingFiles.map((file, index) => (
                                        <span
                                            key={`${file.name}-${index}`}
                                            className={`relative inline-flex items-center rounded-xl bg-primary text-white text-xs font-medium shadow-xs shrink-0 select-none overflow-hidden ${pendingFilePreviewUrls.has(file) ? "h-20 w-32" : "h-9"}`}
                                        >
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handlePendingFileClick(file)
                                                }
                                                className={`relative inline-flex items-center gap-1.5 h-full cursor-pointer hover:bg-white/10 transition-colors min-w-0 text-white bg-transparent border-0 ${pendingFilePreviewUrls.has(file) ? "w-full justify-center p-0" : "max-w-[220px] px-3"}`}
                                                title={file.name}
                                            >
                                                {pendingFilePreviewUrls.has(file) ? (
                                                    <img
                                                        src={pendingFilePreviewUrls.get(file)}
                                                        alt=""
                                                        className="absolute inset-0 h-full w-full rounded-xl object-cover"
                                                    />
                                                ) : (
                                                    <Icon value="paperclip" size={13} className="shrink-0" />
                                                )}
                                                <span className={`truncate ${pendingFilePreviewUrls.has(file) ? "absolute inset-x-0 bottom-0 bg-black/65 px-2 py-1 text-center text-[10px] text-white" : ""}`}>
                                                    {file.name}
                                                </span>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setPendingFiles((files) =>
                                                        files.filter(
                                                            (_, fileIndex) =>
                                                                fileIndex !==
                                                                index
                                                        )
                                                    )
                                                }
                                                className={`cursor-pointer transition-colors text-white/90 hover:text-white shrink-0 bg-transparent border-0 ${pendingFilePreviewUrls.has(file) ? "absolute right-0 top-0 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-black/55 p-1" : "h-full px-2 hover:bg-white/20"}`}
                                                title={t(
                                                    "developments.detail.remove_file"
                                                )}
                                            >
                                                <Icon value="x" size={14} />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Footer: equal-width action buttons */}
                        <div className="px-6 py-3 border-t border-slate-200 dark:border-white/10 bg-white dark:bg-dark-subtle flex items-center justify-center gap-3 shrink-0">
                            <button
                                type="button"
                                onClick={closeComposerFullscreen}
                                className="btn-secondary w-30"
                            >
                                {t("modal.close")}
                            </button>

                            {isIt && (isDesignPhase || isRequirementsPhase) && (
                                <Tooltip title={aiComposerTooltip}>
                                    <button
                                        type="button"
                                        onClick={() => openAiFromComposer(body)}
                                        className="btn-purple min-w-[120px] px-3"
                                        aria-label={aiComposerTooltip}
                                    >
                                        <Icon
                                            value={
                                                isDesignPhase
                                                    ? "palette"
                                                    : "sparkles"
                                            }
                                            size={14}
                                        />
                                        <span>
                                            {isDesignPhase
                                                ? t(
                                                      "developments.detail.generate_design_button"
                                                  )
                                                : t(
                                                      "developments.detail.action_ai"
                                                  )}
                                        </span>
                                    </button>
                                </Tooltip>
                            )}

                            <button
                                type="button"
                                onClick={(e) => {
                                    handleSendMessage(e);
                                    closeComposerFullscreen();
                                }}
                                disabled={sending}
                                className={`btn w-30 ${isInternal ? "warning" : "primary"}`}
                            >
                                {sending ? (
                                    <Icon
                                        value="loader"
                                        size={16}
                                        className="animate-spin"
                                    />
                                ) : (
                                    <Icon value="send" size={16} />
                                )}
                                {t("developments.detail.send")}
                            </button>
                        </div>
                        {renderComposerDropOverlay()}
                    </div>,
                    document.body
                )}

            {/* Confirm Delete Attachment Modal */}
            {isIt && (
                <Modal
                    isOpen={Boolean(attachmentToDelete)}
                    onClose={() => {
                        if (!isDeletingAttachment) {
                            setAttachmentToDelete(null);
                        }
                    }}
                    title={t("developments.detail.delete_attachment_title")}
                    maxWidth="sm"
                    submitVariant="danger"
                    onSubmit={handleConfirmDeleteAttachment}
                    isSubmitting={isDeletingAttachment}
                    submitLabel={t("modal.delete")}
                    submittingLabel={t("modal.deleting")}
                    cancelLabel={t("modal.cancel")}
                >
                    <div className="space-y-3 py-2 text-center">
                        <p className="text-sm text-slate-600 dark:text-zinc-300 leading-relaxed">
                            {t("developments.detail.delete_attachment_desc")}
                        </p>
                        <p className="text-sm font-semibold text-slate-900 dark:text-white truncate px-2">
                            {attachmentToDelete?.name}
                        </p>
                    </div>
                </Modal>
            )}
        </div>
    );

    if (isFullscreen && typeof document !== "undefined") {
        return createPortal(chatFeedContent, document.body);
    }

    return (
        <div className="relative min-w-0 overflow-visible flex-1 flex flex-col h-full min-h-[520px] sm:min-h-[640px] lg:min-h-[700px]">
            {showRightPanelToggle && onToggleRightPanel && (
                <button
                    type="button"
                    onClick={onToggleRightPanel}
                    className="absolute top-[10px] -right-3.5 z-[60] hidden h-7 w-7 cursor-pointer items-center justify-center rounded-full bg-primary text-white shadow-md transition-all hover:scale-110 hover:bg-primary-900 lg:flex"
                    title={
                        isRightPanelOpen
                            ? t("developments.detail.collapse_panel")
                            : t("developments.detail.expand_panel")
                    }
                    aria-expanded={isRightPanelOpen}
                    aria-label={
                        isRightPanelOpen
                            ? t("developments.detail.collapse_panel")
                            : t("developments.detail.expand_panel")
                    }
                >
                    <Icon
                        value={
                            isRightPanelOpen ? "chevronRight" : "chevronLeft"
                        }
                        size={14}
                        className="text-white"
                    />
                </button>
            )}
            {chatFeedContent}
        </div>
    );
};
